import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, API_BASE } from '../lib/api';
import { isReadOnlySession } from '../utils/standaloneUx';
import { type AgentState, getAgentStateFromMessages } from '../lib/agentState';
import { parseDimensionSignals } from '../lib/dimensionSignals';
import AgentAvatar from '../components/AgentAvatar';
import GithubSourceBacklink from '../components/GithubSourceBacklink';
import ChatHistory from '../components/chat/ChatHistory';
import ResumeBanner from '../components/chat/ResumeBanner';
import ChatInput, { type ChatInputHandle } from '../components/chat/ChatInput';
import AgentProgressBar from '../components/chat/AgentProgressBar';
import ArtefactCard from '../components/chat/ArtefactCard';
import { SaveBlockedBanner, parseSaveBlockedEvent, type SaveBlockedInfo } from '../components/chat/SaveBlockedBanner';
import ClaudeDesignRoundTripCard, { claudeDesignCardFromMetadata, wantsClaudeDesignUpload } from '../components/chat/ClaudeDesignRoundTripCard';
import { recoverOutcomeText, ArtefactNotSavedBanner, parseArtefactNotSavedEvent, notSavedFromSessionMetadata, type ArtefactNotSavedInfo, type NotSavedArtefactType } from '../components/chat/ArtefactNotSavedBanner';
import { StructureRebuiltNotice, parseStructureRebuiltEvent, structureRebuiltSourceLabel, type StructureRebuiltArtefactType, type StructureRebuiltInfo } from '../components/chat/StructureRebuiltNotice';

/** #3708 — a copy of `rec` without `key` (the same object when the key is absent). */
function dropKey<T>(rec: Record<string, T>, key: string): Record<string, T> {
  if (!(key in rec)) return rec;
  const next = { ...rec };
  delete next[key];
  return next;
}
import { RegisterEditReportBanner, parseRegisterEditReportEvent, type RegisterEditReportInfo } from '../components/chat/RegisterEditReportBanner';
import { RegisterDeltaLine, parseRegisterDeltaEvent, type RegisterDeltaInfo } from '../components/chat/RegisterDeltaLine';
import SessionHistoryDrawer, { type SessionSummary } from '../components/chat/SessionHistoryDrawer';
import DimensionProgress, { type DevDimensions } from '../components/DimensionProgress';
import ImplementationPromptCard from '../components/ImplementationPromptCard';
import DevReflectionCard, { type DevReflectionCardContent } from '../components/DevReflectionCard';
import AttemptCard, { type AttemptCardContent } from '../components/AttemptCard';
// Issue #855 — Code Review Agent Phase 5 components were shipped in
// PR #847 but never mounted. Wired here.
import PRPickerPanel from '../components/phase5/PRPickerPanel';
import CodeReviewReportCard, { type CodeReviewReport } from '../components/agents/CodeReviewReportCard';
import DevResponsePanel from '../components/DevResponsePanel';
import IssuePicker, { type IssueRow } from '../components/IssuePicker';
import { buildWelcomeBubble } from '../lib/welcomeBubble';

interface SessionMetadata {
  // #3708 — per-type save-block slots; the UX not-saved banners are hydrated from them on load.
  lastSaveBlockByType?: unknown;
  // #3704 — the Claude Design round trip record; the offer card is restored from it on load.
  claudeDesignRoundTrip?: unknown;
  stage?: number;
  dimension?: number;
  complete?: boolean;
  prdArtefactId?: string;
  userStoriesArtefactId?: string;
  sadArtefactId?: string;
  adrArtefactId?: string;
  sprintPlanArtefactId?: string;
  backlogArtefactId?: string;
  changeOrderArtefactId?: string;
  qaReportArtefactId?: string;
  analysisReportArtefactId?: string;
  // Phase 2.5 UX Design artefacts. The API persists each one as the agent
  // produces it (apps/api/src/routes/sessions.ts) but the IDs aren't read
  // back during session restore yet — without these fields ChatPage can't
  // re-render the cards on reload.
  journeyMapArtefactId?: string;
  screenInventoryArtefactId?: string;
  screenSpecArtefactId?: string;
  catalogueArtefactId?: string;
  designTokensArtefactId?: string;
  dimensions?: DevDimensions;
  promptGenerationUnlocked?: boolean;
  promptArtefactId?: string;
  // ACC.A2 (#412) — Dev Agent self-review artefact ID. Persisted by the
  // backend orchestrator after the 3-attempt self-correction loop;
  // restored on session reload so DevReflectionCard re-renders.
  devReflectionArtefactId?: string;
  selectedIssueId?: string | null;
  mode?: string;
}

interface AdrConflictCard {
  id: string;
  adr: string;
  description: string;
}

interface AgentStage {
  number: number;
  name: string;
  /** Short label rendered in the stage bar; full name shows in the tooltip. */
  shortName?: string;
  description: string;
}

interface Session {
  id: string;
  projectId: string;
  agentType: string;
  agentDisplayName?: string;
  agentDescription?: string;
  stages?: AgentStage[];
  // Sprint 11 UX.S1 — chat-render fix. A `sessionId` URL query param can mean
  // either "open this active session" (e.g. dashboard "Generate UX Specs"
  // navigation) or "view this archived session" (e.g. session history
  // dropdown). The session row's status is the authoritative signal —
  // ChatPage uses it to decide whether to render the input or the
  // "End of archived session" footer.
  status?: string;
  summary: string | null;
  tokenCount: number;
  metadata?: SessionMetadata | null;
  createdAt: string;
  welcomeMessage?: string;
  // ACC.A1 #809 (PR #812 Option C) — orientation banner rendered above
  // the chat history when the server's session_checkpoint indicates the
  // dialogue has progressed at least one dimension. Independent of
  // welcomeMessage (which is suppressed on resume by the history-length
  // gate). Null for fresh sessions and for agents without checkpoint
  // label support (UX, Code Review, QA, etc.).
  resumeBanner?: string | null;
}

interface Message {
  id: string;
  sessionId?: string;
  tenantId?: string;
  role: 'user' | 'assistant' | 'system' | 'separator' | 'notification';
  content: string;
  createdAt: string;
  // ACC.B5 (#419) — typed-message marker. NULL on legacy user/assistant
  // rows; non-null on system rows the loop persists. ChatPage filters
  // these out of the chat-history render path and routes them to
  // dedicated state (e.g. setAttempts).
  messageType?: string | null;
}

/**
 * ACC.B5 (#419) — partition raw `agent_messages` rows from
 * GET /:sessionId/messages into:
 *   • `messages` — rows that should render as chat bubbles
 *   • `attempts` — `dev_reflection_attempt` rows decoded into
 *                  AttemptCardContent for the AttemptCard render path
 *
 * Defensive against malformed JSON or schema-drifted shapes in the
 * `content` column — a parse failure or a payload missing required
 * fields drops the row from `attempts` but does NOT throw, so a
 * single corrupted row cannot break the chat history restore. The
 * structural check mirrors what AttemptCard's render path expects;
 * keep these in sync if AttemptCardContent ever grows new required
 * fields.
 */
function isAttemptCardContent(value: unknown): value is AttemptCardContent {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.attemptNumber !== 'number') return false;
  if (v.result !== 'resolved' && v.result !== 'continuing' && v.result !== 'escalated') return false;
  if (typeof v.correctionMade !== 'string') return false;
  const issues = v.issuesIdentified;
  if (!issues || typeof issues !== 'object') return false;
  const i = issues as Record<string, unknown>;
  if (!Array.isArray(i.shortcutsIdentified) || !Array.isArray(i.scopeAdditions)) return false;
  // Round-2 review: validate array element types — AttemptCard's
  // BulletList renders `{item}` directly. A non-string element (object,
  // array, etc.) crashes React with "Objects are not valid as a React
  // child" and tears down the chat pane.
  if (!i.shortcutsIdentified.every((x): x is string => typeof x === 'string')) return false;
  if (!i.scopeAdditions.every((x): x is string => typeof x === 'string')) return false;
  return true;
}

function partitionAgentMessages(rows: Message[]): {
  messages: Message[];
  attempts: AttemptCardContent[];
} {
  const messages: Message[] = [];
  const attempts: AttemptCardContent[] = [];
  for (const row of rows) {
    if (row.messageType === 'dev_reflection_attempt') {
      try {
        const parsed = JSON.parse(row.content);
        if (isAttemptCardContent(parsed)) {
          attempts.push(parsed);
        } else {
          console.error('[ACC.B5] dev_reflection_attempt row failed structural validation; dropping');
        }
      } catch (err) {
        console.error('[ACC.B5] failed to parse dev_reflection_attempt row:', err);
      }
    } else {
      messages.push(row);
    }
  }
  return { messages, attempts };
}

interface ProjectInfo {
  id: string;
  name: string;
  currentPhase?: string;
  projectType?: string;
}

const STREAMING_ID = '__streaming__';

const AGENT_DIMENSIONS: Record<string, number> = {
  architecture: 5,
  sprint_planning: 4,
};

export default function ChatPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const historicalSessionId = searchParams.get('sessionId');
  const requestedAgentType = searchParams.get('agentType');
  const autoAnalyse = searchParams.get('autoAnalyse') === 'true';
  const [project, setProject] = useState<ProjectInfo | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [error, setError] = useState('');
  // ACT.A4 (#1703) — per-tenant session queue indicator. Set when the
  // server responds 202 { status: 'queued', position, estimated_wait_seconds }
  // (session load hardening); cleared once the auto-retry is admitted.
  const [queueStatus, setQueueStatus] = useState<{ position: number; waitSeconds: number } | null>(null);
  const [inputText, setInputText] = useState('');
  const [lastUserContent, setLastUserContent] = useState('');
  const [lastUserFiles, setLastUserFiles] = useState<File[] | undefined>();
  const [agentState, setAgentState] = useState<AgentState>({ stage: 1, dimension: 0, complete: false, approved: false, dimensionsCompleted: [] });
  const [artefactCard, setArtefactCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  const [storiesCard, setStoriesCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  // #3175 (REGSYNC.D) follow-on — lastResyncedAt/resyncReason optional and
  // SAD/ADR-only (the only two card types the register-splice mechanism
  // writes to); undefined on fresh creation (data.sadCreated/adrCreated
  // below) is correct — a just-created artefact has never been resynced.
  // #3382 — set when the server blocked a SAD/ADR save (register override gate / safety net).
  // #3382 / #3708 (ruling 4) — one blocked-save banner per artefact type: an ADR block never
  // replaces a SAD block's banner.
  const [saveBlocked, setSaveBlocked] = useState<Record<string, SaveBlockedInfo>>({});
  // #3708 — one "not saved" notice per UX artefact type; a later save of that type, a
  // successful Recover, or Dismiss removes only that type's notice.
  // #3704 — the Claude Design card under the UX agent's offer (done.claudeDesignOffer, or restored).
  const [claudeDesignCard, setClaudeDesignCard] = useState<'offer' | null>(null);
  const [notSaved, setNotSaved] = useState<Partial<Record<NotSavedArtefactType, ArtefactNotSavedInfo>>>({});
  const dropNotSaved = useCallback((t: NotSavedArtefactType) => setNotSaved(prev => dropKey(prev, t)), []);
  // #3851 — a UX artefact saved this turn with its JSON rebuilt (not the agent's), one notice per type.
  const [structureRebuilt, setStructureRebuilt] = useState<Partial<Record<StructureRebuiltArtefactType, StructureRebuiltInfo>>>({});
  // #3708 (ruling 1) — why a notice closed without recovering (a newer or approved version exists).
  const [recoverNote, setRecoverNote] = useState<string | null>(null);
  const [registerEditReport, setRegisterEditReport] = useState<RegisterEditReportInfo | null>(null);
  const [registerDelta, setRegisterDelta] = useState<RegisterDeltaInfo | null>(null); // #3583
  const [sadCard, setSadCard] = useState<{ artefactId: string; title: string; version: string; status: string; lastResyncedAt?: string | null; resyncReason?: string | null } | null>(null);
  const [adrCard, setAdrCard] = useState<{ artefactId: string; title: string; version: string; status: string; lastResyncedAt?: string | null; resyncReason?: string | null } | null>(null);
  const [sprintPlanCard, setSprintPlanCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  // SL.D1 (#2951, 6406880252 item 7): linkNote — what the save did with the plan (linked to a sprint, or which will take it).
  const [backlogCard, setBacklogCard] = useState<{ artefactId: string; title: string; version: string; status: string; linkNote?: string | null } | null>(null);
  const [changeOrderCard, setChangeOrderCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  const [qaReportCard, setQaReportCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  const [analysisReportCard, setAnalysisReportCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  // Phase 2.5 UX Design artefact cards.
  const [journeyMapCard, setJourneyMapCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  const [screenInventoryCard, setScreenInventoryCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  const [screenSpecCard, setScreenSpecCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  const [componentCatalogueCard, setComponentCatalogueCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  const [designTokensCard, setDesignTokensCard] = useState<{ artefactId: string; title: string; version: string; status: string } | null>(null);
  const [promptCard, setPromptCard] = useState<{ id: string; title: string; content: string } | null>(null);
  // ACC.A2 (#412) — Dev Agent self-review card. Rendered adjacent to
  // the implementation prompt card. Restored on session load via the
  // metadata.devReflectionArtefactId pin (mirrors promptArtefactId);
  // updated live on the SSE 'dev_reflection_created' event.
  const [devReflectionCard, setDevReflectionCard] = useState<{ id: string; title: string; content: DevReflectionCardContent } | null>(null);
  // ACC.B5 (#419) — chronological per-attempt records. Updated live on
  // the `dev_reflection_attempt_created` SSE event during streaming and
  // restored on session reload from agent_messages rows with
  // message_type='dev_reflection_attempt'. Rendered as `AttemptCard`s
  // ahead of the DevReflectionCard summary; also passed to the summary
  // for the three-count derivation.
  const [attempts, setAttempts] = useState<AttemptCardContent[]>([]);
  // Issue #855 — Code Review Report card. Surfaced via the
  // `code_review_report_created` SSE event (already emitted by sessions.ts;
  // wiring the handler is part of this PR). Restored on session resume
  // by the same code_review_report-fetch path the SSE handler uses.
  const [codeReviewCard, setCodeReviewCard] = useState<{ id: string; title: string; content: CodeReviewReport } | null>(null);
  // Staleness tracking
  const [staleArtefacts, setStaleArtefacts] = useState<{ artefactId: string; artefactType: string; reason: string }[]>([]);
  // View Prototype affordance state (NONE/CURRENT/STALE) — distinct from
  // staleArtefacts above, which is PRD-approval staleness, not prototype
  // generation currency.
  const [prototypeStatus, setPrototypeStatus] = useState<{ prototypeExists: boolean; prototypeArtefactId: string | null; prototypeStale: boolean }>({
    prototypeExists: false,
    prototypeArtefactId: null,
    prototypeStale: false,
  });
  // R2 (codex) finding — the initial-load fetch and the post-Regenerate
  // refresh both land on this same setPrototypeStatus with no sequencing:
  // an in-flight initial fetch that resolves AFTER a fast regenerate's
  // refresh would silently put the badge back to stale. requestId makes
  // this monotonic — only the most-recently-ISSUED call's response is ever
  // applied, regardless of resolve order.
  const prototypeStatusRequestId = useRef(0);
  // Shared by the initial-load fetch and the post-Regenerate refresh below —
  // single source for the endpoint so a regenerate flips the stale flag
  // back to current without a full page reload. One bounded retry on
  // failure (also an R2 finding): without it, a transient failure on the
  // post-regenerate refresh leaves the prior — possibly stale — badge
  // showing indefinitely, since the fetch is the only thing that clears it.
  const refreshPrototypeStatus = useCallback(() => {
    if (!projectId) return;
    const requestId = ++prototypeStatusRequestId.current;
    const attempt = (retriesLeft: number) => {
      apiFetch<{ prototypeExists: boolean; prototypeArtefactId: string | null; prototypeStale: boolean }>(
        `/projects/${projectId}/prototype-status`,
      ).then(status => {
        if (prototypeStatusRequestId.current === requestId) setPrototypeStatus(status);
      }).catch(() => {
        if (retriesLeft > 0) setTimeout(() => attempt(retriesLeft - 1), 1500);
      });
    };
    attempt(1);
  }, [projectId]);
  // #2541 — Open Questions affordance count (chat interface). Value comes
  // from GET /:projectId/open-questions/unresolved-count, itself
  // countUnresolvedOpenQuestions (openQuestionsService.ts) — the SAME
  // predicate the Phase-1 Exit Gate blocks on. Never hand-roll a second
  // "unresolved" definition here.
  const [openQuestionsCount, setOpenQuestionsCount] = useState(0);
  const openQuestionsCountRequestId = useRef(0);
  // R2 (codex) finding — ChatPage stays mounted across a projectId change
  // (same route, param swap, no remount). Without resetting here, a slow or
  // failing fetch for the NEW project left the PREVIOUS project's count
  // showing on its card — a stale, wrong-project badge. Reset to 0 (hidden,
  // the safe default) the moment a fresh fetch cycle starts, same
  // absent-is-safer-than-wrong convention as viewPrototypeBtn's NONE state.
  const refreshOpenQuestionsCount = useCallback(() => {
    if (!projectId) return;
    const requestId = ++openQuestionsCountRequestId.current;
    setOpenQuestionsCount(0);
    const attempt = (retriesLeft: number) => {
      apiFetch<{ count: number }>(`/projects/${projectId}/open-questions/unresolved-count`)
        .then(({ count }) => {
          if (openQuestionsCountRequestId.current === requestId) setOpenQuestionsCount(count);
        })
        .catch(() => {
          if (retriesLeft > 0) setTimeout(() => attempt(retriesLeft - 1), 1500);
        });
    };
    attempt(1);
  }, [projectId]);
  // #2544 — Risk-disposition affordance count (chat interface), exact
  // mirror of refreshOpenQuestionsCount above. Value comes from
  // GET /:projectId/risks/undisposed-count, itself countUndisposedRisks
  // (riskDispositionService.ts) — the SAME predicate the Phase-1 Exit Gate
  // blocks on. Never hand-roll a second "undisposed" definition here.
  const [undisposedRisksCount, setUndisposedRisksCount] = useState(0);
  const undisposedRisksCountRequestId = useRef(0);
  const refreshUndisposedRisksCount = useCallback(() => {
    if (!projectId) return;
    const requestId = ++undisposedRisksCountRequestId.current;
    setUndisposedRisksCount(0);
    const attempt = (retriesLeft: number) => {
      apiFetch<{ count: number }>(`/projects/${projectId}/risks/undisposed-count`)
        .then(({ count }) => {
          if (undisposedRisksCountRequestId.current === requestId) setUndisposedRisksCount(count);
        })
        .catch(() => {
          if (retriesLeft > 0) setTimeout(() => attempt(retriesLeft - 1), 1500);
        });
    };
    attempt(1);
  }, [projectId]);
  // #2612 — NFR-disposition affordance count (chat interface), exact
  // mirror of refreshUndisposedRisksCount above. Value comes from
  // GET /:projectId/nfrs/undisposed-count, itself countUndisposedNfrs
  // (nfrDispositionService.ts) — the SAME predicate the Phase-1 Exit Gate
  // blocks on. Never hand-roll a second "undisposed" definition here.
  const [undisposedNfrsCount, setUndisposedNfrsCount] = useState(0);
  const undisposedNfrsCountRequestId = useRef(0);
  const refreshUndisposedNfrsCount = useCallback(() => {
    if (!projectId) return;
    const requestId = ++undisposedNfrsCountRequestId.current;
    setUndisposedNfrsCount(0);
    const attempt = (retriesLeft: number) => {
      apiFetch<{ count: number }>(`/projects/${projectId}/nfrs/undisposed-count`)
        .then(({ count }) => {
          if (undisposedNfrsCountRequestId.current === requestId) setUndisposedNfrsCount(count);
        })
        .catch(() => {
          if (retriesLeft > 0) setTimeout(() => attempt(retriesLeft - 1), 1500);
        });
    };
    attempt(1);
  }, [projectId]);
  // Map artefact types to progress bar stage numbers per agent
  const ARTEFACT_STAGE_MAP: Record<string, Record<string, number>> = {
    architecture:     { sad: 3, adr: 4 },
    pm_requirements:  { prd: 3, user_stories: 4 },
    sprint_planning:  { sprint_plan: 3, sprint_backlog: 4 },
    change_order:     { change_order: 4 },
    // #3708 — the UX agent's artefact-backed stages (stage 4 is approval only, no artefact).
    ux_design:        { user_journey_map: 3, screen_inventory: 5, screen_spec: 6, component_catalogue: 7, design_tokens: 8 },
  };
  // Dev Agent — dimension planning
  const [devDimensions, setDevDimensions] = useState<DevDimensions | null>(null);
  const [promptGenerationUnlocked, setPromptGenerationUnlocked] = useState(false);
  const [adrConflictCards, setAdrConflictCards] = useState<AdrConflictCard[]>([]);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [codeGenComplete, setCodeGenComplete] = useState(false);
  const [openIssues, setOpenIssues] = useState<IssueRow[] | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<IssueRow | null>(null);
  const [changingIssue, setChangingIssue] = useState(false);
  const [changeIssueModalOpen, setChangeIssueModalOpen] = useState(false);
  const [changeIssueChoice, setChangeIssueChoice] = useState<'switch' | 'new'>('new');
  const [creatingNewSession, setCreatingNewSession] = useState(false);
  // Session history drawer
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [sessionList, setSessionList] = useState<SessionSummary[]>([]);
  const [sessionListLoading, setSessionListLoading] = useState(false);
  const [newChatConfirmOpen, setNewChatConfirmOpen] = useState(false);
  // FT-IMPORT Story 2 — provenance backlink source ("owner/repo#N") of the FT
  // issue when imported from GitHub; null for manual / standard projects.
  const [importedSource, setImportedSource] = useState<string | null>(null);
  const navigate = useNavigate();
  const inputRef = useRef<ChatInputHandle>(null);
  // Chat overlap fix — the bottom stack (composer, archived footer, dev-agent
  // Continue/Skip row) is measured as ONE element and its live height is
  // published as --chat-bottom-stack-height on :root. HelpCaptureEntry (a
  // fixed "?" button mounted by the layout, not inside this page) reads it to
  // sit above the send button. It lives on :root because that button is not
  // a descendant of this page; it is removed on unmount (and whenever the
  // stack itself unmounts, e.g. the no-agent screen) so no other page ever
  // sees it. State rather than a ref object so the observer re-attaches when
  // the stack mounts after the initial render.
  const [bottomStackEl, setBottomStackEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!bottomStackEl) return;
    const root = document.documentElement;
    const publish = () => {
      root.style.setProperty('--chat-bottom-stack-height', `${bottomStackEl.offsetHeight}px`);
    };
    publish();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    observer?.observe(bottomStackEl);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--chat-bottom-stack-height');
    };
  }, [bottomStackEl]);
  const abortControllerRef = useRef<AbortController | null>(null);
  // ACT.A4 (#1703) — pending auto-retry timer scheduled after a 202
  // { status: 'queued' } response. Tracked so it can be cancelled: on
  // unmount, on a fresh manual send, and via handleStop (the
  // AbortController above is already-completed by the time this timer is
  // pending, so it alone can't cancel a queued retry).
  const queueRetryTimeoutRef = useRef<number | null>(null);
  const initialized = useRef(false);

  // ACT.A4 (#1703) — clear any pending queued-retry timer on unmount so it
  // never fires (and calls setState / issues a network request) against a
  // page the user has navigated away from.
  useEffect(() => {
    return () => {
      if (queueRetryTimeoutRef.current !== null) {
        clearTimeout(queueRetryTimeoutRef.current);
        queueRetryTimeoutRef.current = null;
      }
    };
  }, []);

  // Focus the input after the initial page load completes
  useEffect(() => {
    if (!loading) inputRef.current?.focus();
  }, [loading]);

  // Re-focus the input after each streaming response finishes
  useEffect(() => {
    if (!sending) inputRef.current?.focus();
  }, [sending]);

  // FT-IMPORT Story 2 — best-effort fetch of the FT issue's github_source for
  // the "Imported from owner/repo#N" header backlink. Isolated + fail-soft: it
  // never blocks or breaks the chat if it errors, and renders nothing when the
  // project has no imported issue (manual FT / standard projects).
  useEffect(() => {
    // Eager-clear so a previous project's backlink never lingers across project
    // navigation or after a failed fetch. Only Fast Track projects can carry an
    // imported issue, so skip the issues fetch entirely for standard projects
    // (avoids loading a potentially large issue list to read one field).
    setImportedSource(null);
    if (!projectId || project?.projectType !== 'fast_track') return;
    let cancelled = false;
    apiFetch<Array<{ githubSource?: string | null }>>(`/projects/${projectId}/issues`)
      .then(rows => { if (!cancelled) setImportedSource(rows.find(r => r.githubSource)?.githubSource ?? null); })
      .catch(() => { /* provenance is non-essential — ignore */ });
    return () => { cancelled = true; };
  }, [projectId, project?.projectType]);

  // On mount: load project info + create/resume session + load message history
  // Guard against React StrictMode double-invocation
  useEffect(() => {
    if (!projectId) return;
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        const restoreArtefact = async (artefactId: string) => {
          const a = await apiFetch<{
            id: string;
            title: string;
            status: string;
            latestVersion: { versionNumber: string } | null;
            // #3175 (REGSYNC.D) follow-on — present on every artefact row
            // (migration 0245); only ever non-null for sad/adr in practice,
            // but harmless to carry through for every card type.
            lastResyncedAt?: string | null;
            resyncReason?: string | null;
          }>(`/artefacts/${artefactId}`);
          return {
            artefactId: a.id,
            title: a.title,
            version: a.latestVersion?.versionNumber ?? '1.0',
            status: a.status,
            lastResyncedAt: a.lastResyncedAt ?? null,
            resyncReason: a.resyncReason ?? null,
          };
        };
        const restorePromptCard = async (artefactId: string) => {
          const a = await apiFetch<{
            id: string;
            title: string;
            latestVersion: { notes: string } | null;
          }>(`/artefacts/${artefactId}`);
          return { id: a.id, title: a.title, content: a.latestVersion?.notes ?? '' };
        };
        // ACC.A2 (#412) — restore DevReflectionCard from a persisted
        // artefactId. Returns null if the JSONB content can't be parsed
        // (defensive against schema drift); the caller treats null as
        // "no card to render" rather than throwing.
        const restoreDevReflectionCard = async (artefactId: string) => {
          try {
            const a = await apiFetch<{
              id: string;
              title: string;
              latestVersion: { notes: string } | null;
            }>(`/artefacts/${artefactId}`);
            const raw = a.latestVersion?.notes ?? '';
            if (!raw) return null;
            const content = JSON.parse(raw) as DevReflectionCardContent;
            return { id: a.id, title: a.title, content };
          } catch {
            return null;
          }
        };

        if (historicalSessionId && !autoAnalyse) {
          // Open a specific session by ID. This branch fires from two places:
          //   • Dashboard "Generate UX Specs" — opens a freshly-created
          //     active standalone session (Sprint 11 UX.S1).
          //   • Session history dropdown — opens an old completed session.
          // The session row's status is the source of truth; treat anything
          // other than 'active' as read-only history.
          const [proj, sess] = await Promise.all([
            apiFetch<ProjectInfo>(`/projects/${projectId}`),
            apiFetch<Session>(`/sessions/${historicalSessionId}`),
          ]);
          // Defence-in-depth: refuse to render a session that doesn't
          // belong to the route project. The GET /sessions/:id endpoint
          // doesn't filter by project, so without this guard a URL like
          // /projects/<A>/chat?sessionId=<B's active session> would let
          // the user write into the wrong project's context — the chat
          // header reads from `proj` while the message POST goes against
          // `sess.id`.
          if (sess.projectId !== projectId) {
            throw new Error('Session does not belong to this project');
          }
          setProject(proj);
          setSession(sess);
          const readOnly = isReadOnlySession(sess.status);
          const isActiveSession = !readOnly;
          setIsReadOnly(readOnly);
          const history = await apiFetch<Message[]>(`/sessions/${sess.id}/messages`);
          // ACC.B5 (#419) — split typed system messages out of the chat
          // history before rendering. dev_reflection_attempt rows carry
          // structured JSON the AttemptCard renders, not chat copy.
          const partitioned = partitionAgentMessages(history);
          if (partitioned.attempts.length > 0) setAttempts(partitioned.attempts);
          // Active sessions with no messages yet show the agent welcome
          // bubble (mirroring the create/resume branch); read-only sessions
          // show whatever history they have.
          if (isActiveSession && partitioned.messages.length === 0) {
            setMessages([buildWelcomeBubble(sess.welcomeMessage)]);
          } else {
            setMessages(partitioned.messages);
          }
          if (sess.metadata?.stage) {
            setAgentState({
              stage: sess.metadata.stage,
              dimension: sess.metadata.dimension ?? 0,
              complete: sess.metadata.complete ?? false,
              approved: false,
              dimensionsCompleted: [],
            });
          }
          // Parallelise the artefact-card restorations. Each one is an
          // independent /artefacts/:id GET; the previous sequential awaits
          // could chain 10+ round-trips for an old session and each setState
          // forced ChatHistory to re-run its scroll-to-bottom effect during
          // the heavy initial render.
          const m = sess.metadata ?? {};
          // #3708 (R1 P1-1) — the not-saved banners survive a reload: hydrated from the per-type slots.
          if (sess.agentType === 'ux_design') setNotSaved(notSavedFromSessionMetadata(m));
          if (sess.agentType === 'ux_design') setClaudeDesignCard(claudeDesignCardFromMetadata(m));
          // R1 P2 / R2 P2-2 (#3871) — one card that fails to load is skipped; it no longer fails the whole chat.
          await Promise.all([
            m.prdArtefactId                  ? restoreArtefact(m.prdArtefactId).then(setArtefactCard).catch(() => {}) : null,
            m.userStoriesArtefactId          ? restoreArtefact(m.userStoriesArtefactId).then(setStoriesCard).catch(() => {}) : null,
            m.sadArtefactId                  ? restoreArtefact(m.sadArtefactId).then(setSadCard).catch(() => {}) : null,
            m.adrArtefactId                  ? restoreArtefact(m.adrArtefactId).then(setAdrCard).catch(() => {}) : null,
            m.sprintPlanArtefactId           ? restoreArtefact(m.sprintPlanArtefactId).then(setSprintPlanCard).catch(() => {}) : null,
            m.backlogArtefactId              ? restoreArtefact(m.backlogArtefactId).then(setBacklogCard).catch(() => {}) : null,
            m.changeOrderArtefactId          ? restoreArtefact(m.changeOrderArtefactId).then(setChangeOrderCard).catch(() => {}) : null,
            m.qaReportArtefactId             ? restoreArtefact(m.qaReportArtefactId).then(setQaReportCard).catch(() => {}) : null,
            m.analysisReportArtefactId       ? restoreArtefact(m.analysisReportArtefactId).then(setAnalysisReportCard).catch(() => {}) : null,
            m.journeyMapArtefactId           ? restoreArtefact(m.journeyMapArtefactId).then(setJourneyMapCard).catch(() => {}) : null,
            m.screenInventoryArtefactId      ? restoreArtefact(m.screenInventoryArtefactId).then(setScreenInventoryCard).catch(() => {}) : null,
            m.screenSpecArtefactId           ? restoreArtefact(m.screenSpecArtefactId).then(setScreenSpecCard).catch(() => {}) : null,
            m.catalogueArtefactId   ? restoreArtefact(m.catalogueArtefactId).then(setComponentCatalogueCard).catch(() => {}) : null,
            m.designTokensArtefactId         ? restoreArtefact(m.designTokensArtefactId).then(setDesignTokensCard).catch(() => {}) : null,
            m.promptArtefactId               ? restorePromptCard(m.promptArtefactId).then(setPromptCard).catch(() => {}) : null,
            // ACC.A2 (#412) — restore the dev_reflection card alongside
            // the implementation prompt card so a reload preserves both.
            m.devReflectionArtefactId        ? restoreDevReflectionCard(m.devReflectionArtefactId).then(setDevReflectionCard).catch(() => {}) : null,
          ].filter(Boolean));
          if (sess.metadata?.dimensions) setDevDimensions(sess.metadata.dimensions);
          if (sess.metadata?.promptGenerationUnlocked) setPromptGenerationUnlocked(true);
        } else if (historicalSessionId && autoAnalyse) {
          // Gap analysis mode — load the pre-created session, then auto-send first message
          const [proj, sess] = await Promise.all([
            apiFetch<ProjectInfo>(`/projects/${projectId}`),
            apiFetch<Session>(`/sessions/${historicalSessionId}`),
          ]);
          // Same defence-in-depth as the read-only branch above.
          if (sess.projectId !== projectId) {
            throw new Error('Session does not belong to this project');
          }
          setProject(proj);
          setSession(sess);
          // Check if the session already has messages (e.g. page refresh) — skip auto-send
          const history = await apiFetch<Message[]>(`/sessions/${sess.id}/messages`);
          // ACC.B5 (#419) — see note at the historicalSessionId branch above.
          const partitioned = partitionAgentMessages(history);
          if (partitioned.attempts.length > 0) setAttempts(partitioned.attempts);
          if (partitioned.messages.length > 0) {
            setMessages(partitioned.messages);
            if (sess.metadata?.analysisReportArtefactId) setAnalysisReportCard(await restoreArtefact(sess.metadata.analysisReportArtefactId).catch(() => null));
          } else {
            setMessages([buildWelcomeBubble(sess.welcomeMessage)]);
          }
        } else {
          // Normal flow: create/resume current-phase session.
          // Fetch project first so it's available even if session creation fails (e.g. no agent for phase).
          const proj = await apiFetch<ProjectInfo>(`/projects/${projectId}`);
          setProject(proj);
          const sessionBody = requestedAgentType ? JSON.stringify({ agentType: requestedAgentType }) : '{}';
          const sessResp = await apiFetch<Session & { openIssues?: IssueRow[]; sprintSummary?: unknown }>(
            `/projects/${projectId}/sessions`, { method: 'POST', body: sessionBody },
          );
          setSession(sessResp);
          // Initialise Dev Agent dimension tracker
          if (sessResp.agentType === 'dev_agent') {
            setDevDimensions(sessResp.metadata?.dimensions ?? {
              codebaseContext:        { complete: false, summary: null, skipped: false },
              implementationApproach: { complete: false, summary: null, skipped: false, adrConflicts: [] },
              acceptanceCriteria:     { complete: false, summary: null, skipped: false, edgeCases: [] },
            });
            setPromptGenerationUnlocked(sessResp.metadata?.promptGenerationUnlocked ?? false);
            // Store open issues for IssuePicker (returned by both create and resume)
            if (sessResp.openIssues) setOpenIssues(sessResp.openIssues);
            // Fetch selected issue details for SelectedIssueCard on resume
            if (sessResp.metadata?.selectedIssueId) {
              apiFetch<IssueRow>(`/issues/${sessResp.metadata.selectedIssueId}`)
                .then(issue => setSelectedIssue(issue))
                .catch(() => {});
            }
          }
          // ACC.A3 (#413, absorbs #816) — Code Review Agent gets the
          // dev_reflection-filtered issue list via the same POST
          // /sessions response shape. Mirrors the Dev Agent path so
          // ChatPage's IssuePicker / SelectedIssueCard render logic
          // (gated on agentType ∈ {dev_agent, code_review}) sees the
          // same state shape regardless of which agent owns the session.
          if (sessResp.agentType === 'code_review') {
            if (sessResp.openIssues) setOpenIssues(sessResp.openIssues);
            if (sessResp.metadata?.selectedIssueId) {
              apiFetch<IssueRow>(`/issues/${sessResp.metadata.selectedIssueId}`)
                .then(issue => setSelectedIssue(issue))
                .catch(() => {});
            }
          }
          const sess = sessResp;

          const history = await apiFetch<Message[]>(`/sessions/${sess.id}/messages`);
          // ACC.B5 (#419) — see note at the historicalSessionId branch above.
          const partitioned = partitionAgentMessages(history);
          if (partitioned.attempts.length > 0) setAttempts(partitioned.attempts);
          if (partitioned.messages.length > 0) {
            setMessages(partitioned.messages);
            // Prefer metadata for accurate agentState restoration; fall back to scanning history
            if (sess.metadata?.stage) {
              setAgentState({
                stage: sess.metadata.stage,
                dimension: sess.metadata.dimension ?? 0,
                complete: sess.metadata.complete ?? false,
                approved: false,
                dimensionsCompleted: [],
              });
            } else {
              setAgentState(getAgentStateFromMessages(partitioned.messages));
            }
            // #3708 (R1 P1-1) — hydrate the not-saved banners from the per-type slots.
            if (sess.agentType === 'ux_design') setNotSaved(notSavedFromSessionMetadata(sess.metadata));
            if (sess.agentType === 'ux_design') setClaudeDesignCard(claudeDesignCardFromMetadata(sess.metadata));
            // Restore artefact cards from session metadata
            if (sess.metadata?.prdArtefactId) setArtefactCard(await restoreArtefact(sess.metadata.prdArtefactId).catch(() => null));
            if (sess.metadata?.userStoriesArtefactId) setStoriesCard(await restoreArtefact(sess.metadata.userStoriesArtefactId).catch(() => null));
            if (sess.metadata?.sadArtefactId) setSadCard(await restoreArtefact(sess.metadata.sadArtefactId).catch(() => null));
            if (sess.metadata?.adrArtefactId) setAdrCard(await restoreArtefact(sess.metadata.adrArtefactId).catch(() => null));
            if (sess.metadata?.sprintPlanArtefactId) setSprintPlanCard(await restoreArtefact(sess.metadata.sprintPlanArtefactId).catch(() => null));
            if (sess.metadata?.backlogArtefactId) setBacklogCard(await restoreArtefact(sess.metadata.backlogArtefactId).catch(() => null));
            if (sess.metadata?.changeOrderArtefactId) setChangeOrderCard(await restoreArtefact(sess.metadata.changeOrderArtefactId).catch(() => null));
            if (sess.metadata?.qaReportArtefactId) setQaReportCard(await restoreArtefact(sess.metadata.qaReportArtefactId).catch(() => null));
            if (sess.metadata?.analysisReportArtefactId) setAnalysisReportCard(await restoreArtefact(sess.metadata.analysisReportArtefactId).catch(() => null));
            if (sess.metadata?.journeyMapArtefactId) setJourneyMapCard(await restoreArtefact(sess.metadata.journeyMapArtefactId).catch(() => null));
            if (sess.metadata?.screenInventoryArtefactId) setScreenInventoryCard(await restoreArtefact(sess.metadata.screenInventoryArtefactId).catch(() => null));
            if (sess.metadata?.screenSpecArtefactId) setScreenSpecCard(await restoreArtefact(sess.metadata.screenSpecArtefactId).catch(() => null));
            if (sess.metadata?.catalogueArtefactId) setComponentCatalogueCard(await restoreArtefact(sess.metadata.catalogueArtefactId).catch(() => null));
            if (sess.metadata?.designTokensArtefactId) setDesignTokensCard(await restoreArtefact(sess.metadata.designTokensArtefactId).catch(() => null));
            if (sess.metadata?.promptArtefactId) setPromptCard(await restorePromptCard(sess.metadata.promptArtefactId).catch(() => null));
            // ACC.A2 (#412) — restore the dev_reflection card on the
            // dashboard "Generate UX Specs" entry path (mirrors the
            // promptArtefactId restore above).
            if (sess.metadata?.devReflectionArtefactId) {
              const card = await restoreDevReflectionCard(sess.metadata.devReflectionArtefactId);
              if (card) setDevReflectionCard(card);
            }
            if (sess.metadata?.dimensions) setDevDimensions(sess.metadata.dimensions);
            if (sess.metadata?.promptGenerationUnlocked) setPromptGenerationUnlocked(true);
          } else {
            setMessages([buildWelcomeBubble(sessResp.welcomeMessage)]);
            // #3871 — a new chat has no history, but its metadata already carries the project's
            // existing artefacts (attached read-only by the session route), so their cards — and
            // the stages they tick — show before the first message instead of after a reload. A card that
            // fails to load is skipped (a new chat must still open).
            const m = sess.metadata ?? {};
            await Promise.all([
              m.prdArtefactId             ? restoreArtefact(m.prdArtefactId).then(setArtefactCard).catch(() => {}) : null,
              m.userStoriesArtefactId     ? restoreArtefact(m.userStoriesArtefactId).then(setStoriesCard).catch(() => {}) : null,
              m.sadArtefactId             ? restoreArtefact(m.sadArtefactId).then(setSadCard).catch(() => {}) : null,
              m.adrArtefactId             ? restoreArtefact(m.adrArtefactId).then(setAdrCard).catch(() => {}) : null,
              m.journeyMapArtefactId      ? restoreArtefact(m.journeyMapArtefactId).then(setJourneyMapCard).catch(() => {}) : null,
              m.screenInventoryArtefactId ? restoreArtefact(m.screenInventoryArtefactId).then(setScreenInventoryCard).catch(() => {}) : null,
              m.screenSpecArtefactId      ? restoreArtefact(m.screenSpecArtefactId).then(setScreenSpecCard).catch(() => {}) : null,
              m.catalogueArtefactId       ? restoreArtefact(m.catalogueArtefactId).then(setComponentCatalogueCard).catch(() => {}) : null,
              m.designTokensArtefactId    ? restoreArtefact(m.designTokensArtefactId).then(setDesignTokensCard).catch(() => {}) : null,
            ].filter(Boolean));
          }
        }
        // Fetch staleness data for progress bar and artefact cards
        apiFetch<{ artefactId: string; artefactType: string; reason: string }[]>(
          `/projects/${projectId}/staleness`,
        ).then(setStaleArtefacts).catch(() => {});
        // Fetch prototype status for the "View Prototype" card affordance
        refreshPrototypeStatus();
        // #2541 — fetch the Open Questions count for the chat affordance
        refreshOpenQuestionsCount();
        // #2544 — fetch the Risk-disposition count for the chat affordance
        refreshUndisposedRisksCount();
        // #2612 — fetch the NFR-disposition count for the chat affordance
        refreshUndisposedNfrsCount();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load chat');
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const handleInputChange = (text: string) => {
    setInputText(text);
    // sessionStorage persistence is handled inside ChatInput using its sessionId prop
  };

  // ACT.A4 (#1703) — `isRetry` is set when this call is the automatic
  // re-POST after a 202 { status: 'queued' } response. On a retry we must
  // NOT re-add the optimistic user bubble (it's already on screen from the
  // original attempt) — only the streaming placeholder, if not still present.
  const handleSend = async (content: string, files?: File[], isRetry = false) => {
    if (!session) return;
    // ACT.A4 (#1703) — a fresh manual send supersedes any still-pending
    // queued-retry timer from a PRIOR message; cancel it so it can't fire
    // later with its stale content/files closure and interleave with (or
    // duplicate) this new send.
    if (!isRetry && queueRetryTimeoutRef.current !== null) {
      clearTimeout(queueRetryTimeoutRef.current);
      queueRetryTimeoutRef.current = null;
    }
    setSending(true);
    setError('');
    setSaveBlocked({});
    setRegisterEditReport(null);
    setRegisterDelta(null);
    setStructureRebuilt({}); // #3851 (R2 P3) — per-turn, like the save-blocked notice
    setLastUserContent(content);
    setLastUserFiles(files);

    // Build attachment marker for optimistic display
    const attachmentMarker =
      files && files.length > 0
        ? ` [Attached: ${files.map(f => f.name).join(', ')}]`
        : '';

    // Optimistic user bubble
    const tempUserId = `__opt_${Date.now()}`;
    const optimisticUser: Message = {
      id: tempUserId,
      role: 'user',
      content: content + attachmentMarker,
      createdAt: new Date().toISOString(),
    };
    // Streaming placeholder for assistant
    const streamingPlaceholder: Message = {
      id: STREAMING_ID,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
    };

    if (!isRetry) {
      setMessages(prev => [
        ...prev.filter(m => m.id !== '__welcome__'),
        optimisticUser,
        streamingPlaceholder,
      ]);
    }

    // ACT.A4 (#1703) — set when this attempt ends by scheduling a queued
    // retry, so `finally` below knows NOT to clear `sending` — the Stop
    // button (gated on `sending`) must stay live for the whole queued wait,
    // not just the initial fetch, so handleStop can actually cancel it.
    let willRetry = false;

    try {
      const token = localStorage.getItem('blueprint_token');

      let body: BodyInit;
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

      if (files && files.length > 0) {
        const fd = new FormData();
        fd.append('content', content);
        for (const file of files) fd.append('files', file);
        body = fd;
        // Do NOT set Content-Type for FormData — browser sets it with boundary
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ content, role: 'user' });
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const response = await fetch(`${API_BASE}/sessions/${session.id}/chat`, {
        method: 'POST',
        headers,
        body,
        signal: abortController.signal,
      });

      // ACT.A4 (#1703) — 202 means the server queued this turn (per-tenant
      // session load hardening); it is a plain JSON body, not an SSE stream.
      // Show the queue position and automatically retry once the estimated
      // wait has elapsed, rather than treating this as an error.
      if (response.status === 202) {
        const queueInfo = await response.json().catch(() => null) as
          { status?: string; position?: number; estimated_wait_seconds?: number } | null;
        setMessages(prev => prev.filter(m => m.id !== STREAMING_ID));
        const position = queueInfo?.position ?? 1;
        const waitSeconds = queueInfo?.estimated_wait_seconds ?? 1;
        setQueueStatus({ position, waitSeconds });
        const retryDelayMs = Math.max(1000, waitSeconds * 1000);
        willRetry = true;
        queueRetryTimeoutRef.current = window.setTimeout(() => {
          queueRetryTimeoutRef.current = null;
          setQueueStatus(null);
          void handleSend(content, files, true);
        }, retryDelayMs);
        return;
      }
      setQueueStatus(null);

      if (!response.ok) {
        const body = await response.json().catch(() => ({})) as Record<string, unknown>;
        if (response.status === 402 && body.error === 'TOKEN_ALLOWANCE_EXHAUSTED') {
          window.dispatchEvent(new CustomEvent('token-exhaustion', { detail: body }));
        }
        throw new Error((body as { error?: string }).error ?? `Request failed: ${response.status}`);
      }

      if (!response.body) throw new Error('Streaming not supported by this environment');
      // A retry that lands here (admitted after previously being queued)
      // needs its streaming placeholder re-added — it was removed above
      // when the queued banner replaced it.
      setMessages(prev =>
        prev.some(m => m.id === STREAMING_ID) ? prev : [...prev, streamingPlaceholder],
      );
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() ?? '';

        for (const chunk of chunks) {
          const line = chunk.trim();
          if (!line.startsWith('data: ')) continue;

          const data = JSON.parse(line.slice(6)) as {
            type: string;
            text?: string;
            userMessage?: Message;
            assistantMessage?: Message;
            agentState?: AgentState;
            artefactCreated?: { artefactId: string; title: string; version: string };
            storiesCreated?: { artefactId: string; title: string; version: string };
            sadCreated?: { artefactId: string; title: string; version: string };
            adrCreated?: { artefactId: string; title: string; version: string };
            sprintPlanCreated?: { artefactId: string; title: string; version: string };
            backlogCreated?: { artefactId: string; title: string; version: string; linkNote?: string | null };
            changeOrderCreated?: { artefactId: string; title: string; version: string };
            qaReportCreated?: { artefactId: string; title: string; version: string };
            analysisReportCreated?: { artefactId: string; title: string; version: string };
            journeyMapCreated?: { artefactId: string; title: string; version: string };
            screenInventoryCreated?: { artefactId: string; title: string; version: string };
            screenSpecCreated?: { artefactId: string; title: string; version: string };
            componentCatalogueCreated?: { artefactId: string; title: string; version: string };
            designTokensCreated?: { artefactId: string; title: string; version: string };
            // Server-side auto-approvals — emitted by the UX Agent (and any
            // future agent) when the user confirmed approval in chat. Each
            // entry tells the client to flip the matching card from `draft`
            // to `approved` so the handoff pill / next-step button appears
            // without a reload.
            artefactsApproved?: Array<{ artefactId: string; type: string; title: string }>;
            // #3704 — the Claude Design offer was made this turn: show the card.
            claudeDesignOffer?: boolean;
            // #3704 final delta P2-1 — the server recorded this turn's Skip: hide the card.
            claudeDesignSkipped?: boolean;
            implementationPromptCreated?: { artefactId: string; title: string; promptContent: string };
            artefactId?: string;
            title?: string;
            promptContent?: string;
            // ACC.A2 (#412) — additional fields on the 'dev_reflection_created'
            // SSE event. Optional because most events don't carry them; the
            // handler narrows on data.type === 'dev_reflection_created'.
            selfAssessment?: 'READY_FOR_REVIEW' | 'NEEDS_REWORK';
            selfCorrectionAttempts?: number;
            // ACC.B5 (#419) — `dev_reflection_attempt_created` payload.
            // The handler narrows on data.type for type-safe access.
            attempt?: AttemptCardContent;
            dimensionsUpdated?: DevDimensions;
            promptGenerationUnlocked?: boolean;
            message?: string;
            // #3382 — register_override_blocked / register_conflict_detected
            artefactType?: string;
            undeclaredCitations?: Array<{ handle: string }>;
            rejected?: RegisterEditReportInfo['rejected'];
            overridden?: RegisterEditReportInfo['overridden'];
            agentNotified?: boolean;
            conflicts?: Array<{ handle: string; reason?: string | null }>;
            // #3708 — artefact_not_saved
            reason?: string;
            savedVersionExists?: boolean;
            recoverable?: boolean;
            // #3851 — artefact_structure_rebuilt
            rebuiltFrom?: string;
          };

          if (data.type === 'delta' && data.text !== undefined) {
            setMessages(prev =>
              prev.map(m =>
                m.id === STREAMING_ID ? { ...m, content: m.content + data.text! } : m,
              ),
            );
          } else if (data.type === 'continuation_start') {
            // Auto-continuation — keep appending to the same streaming bubble (no-op)
            // The subsequent delta events will continue accumulating into STREAMING_ID
          } else if (data.type === 'implementation_prompt_created' && data.artefactId && data.title && data.promptContent !== undefined) {
            // Render the prompt card immediately on receipt — the redundant payload
            // on 'done' is a fallback if this event was missed.
            setPromptCard({
              id: data.artefactId,
              title: data.title,
              content: data.promptContent,
            });
          } else if (data.type === 'dev_reflection_created' && data.artefactId
                     && (data.selfAssessment === 'READY_FOR_REVIEW' || data.selfAssessment === 'NEEDS_REWORK')
                     && typeof data.selfCorrectionAttempts === 'number') {
            // ACC.A2 (#412) — dev_reflection landed live during the
            // streaming session. The SSE payload carries headline fields
            // (artefactId, selfAssessment, selfCorrectionAttempts); the
            // full JSONB content (acsCovered, shortcuts, scopeAdditions)
            // needs a follow-up fetch. Inlined here because the resume
            // effect's restoreDevReflectionCard helper is in a different
            // closure scope; extracting to module scope would require a
            // wider refactor.
            const reflectionArtefactId = data.artefactId;
            void (async () => {
              try {
                const a = await apiFetch<{
                  id: string;
                  title: string;
                  latestVersion: { notes: string } | null;
                }>(`/artefacts/${reflectionArtefactId}`);
                const raw = a.latestVersion?.notes ?? '';
                if (!raw) return;
                const content = JSON.parse(raw) as DevReflectionCardContent;
                setDevReflectionCard({ id: a.id, title: a.title, content });
              } catch (err) {
                console.error('[ACC.A2] dev_reflection card fetch failed:', err);
              }
            })();
          } else if (data.type === 'dev_reflection_attempt_created' && data.attempt) {
            // ACC.B5 (#419) — append the attempt record to state. Order
            // is chronological by emit time (the loop emits in order),
            // so a simple append preserves the visual sequence the user
            // sees in the chat. Persistence has already committed
            // server-side (see persistAttempt's ordering), so a reload
            // mid-stream will pick up the same row from agent_messages
            // via the partitionAgentMessages restore path.
            //
            // Round-2 review: apply the same isAttemptCardContent guard
            // here as on the restore path — a server-side schema drift
            // would otherwise crash the chat with "Objects are not
            // valid as a React child" inside AttemptCard's BulletList.
            // Drop the malformed event with a console error rather
            // than render it.
            if (isAttemptCardContent(data.attempt)) {
              const incoming = data.attempt;
              setAttempts((prev) => [...prev, incoming]);
            } else {
              console.error('[ACC.B5] dev_reflection_attempt_created SSE event failed structural validation; dropping');
            }
          } else if (data.type === 'code_review_report_created' && data.artefactId && data.title) {
            // Issue #855 — Code Review Report SSE handler. Mirrors the
            // dev_reflection_created branch: fetch the artefact's
            // latestVersion.notes (JSON-stringified CodeReviewReport)
            // and render via CodeReviewReportCard. The event itself
            // carries only artefactId + title; the heavy JSONB content
            // is fetched on receipt.
            const reportArtefactId = data.artefactId;
            void (async () => {
              try {
                const a = await apiFetch<{
                  id: string;
                  title: string;
                  latestVersion: { notes: string } | null;
                }>(`/artefacts/${reportArtefactId}`);
                const raw = a.latestVersion?.notes ?? '';
                if (!raw) return;
                const content = JSON.parse(raw) as CodeReviewReport;
                setCodeReviewCard({ id: a.id, title: a.title, content });
              } catch (err) {
                console.error('[#855] code_review_report card fetch failed:', err);
              }
            })();
          } else if (data.type === 'done' && data.userMessage && data.assistantMessage) {
            // Parse dimension signals from assistant content (Dev Agent only)
            const raw = data.assistantMessage!.content;
            const signals = parseDimensionSignals(raw);
            const cleanedMessage = { ...data.assistantMessage!, content: signals.strippedContent };

            setMessages(prev => [
              ...prev.filter(m => m.id !== tempUserId && m.id !== STREAMING_ID),
              data.userMessage!,
              cleanedMessage,
            ]);
            if (data.agentState) setAgentState(data.agentState);
            if (data.artefactCreated) setArtefactCard({ ...data.artefactCreated, status: 'draft' });
            if (data.storiesCreated) setStoriesCard({ ...data.storiesCreated, status: 'draft' });
            if (data.sadCreated) setSadCard({ ...data.sadCreated, status: 'draft' });
            if (data.adrCreated) setAdrCard({ ...data.adrCreated, status: 'draft' });
            // #3382 — a save that went through supersedes an earlier block notice for that type.
            if (data.sadCreated) setSaveBlocked(prev => dropKey(prev, 'sad'));
            if (data.adrCreated) setSaveBlocked(prev => dropKey(prev, 'adr'));
            if (data.sprintPlanCreated) setSprintPlanCard({ ...data.sprintPlanCreated, status: 'draft' });
            if (data.backlogCreated) setBacklogCard({ ...data.backlogCreated, status: 'draft' });
            if (data.changeOrderCreated) setChangeOrderCard({ ...data.changeOrderCreated, status: 'draft' });
            if (data.qaReportCreated) setQaReportCard({ ...data.qaReportCreated, status: 'draft' });
            if (data.analysisReportCreated) setAnalysisReportCard({ ...data.analysisReportCreated, status: 'draft' });
            if (data.journeyMapCreated) setJourneyMapCard({ ...data.journeyMapCreated, status: 'draft' });
            if (data.screenInventoryCreated) setScreenInventoryCard({ ...data.screenInventoryCreated, status: 'draft' });
            if (data.screenSpecCreated) setScreenSpecCard({ ...data.screenSpecCreated, status: 'draft' });
            if (data.componentCatalogueCreated) setComponentCatalogueCard({ ...data.componentCatalogueCreated, status: 'draft' });
            // #3708 — a save that went through supersedes that type's "not saved" notice.
            if (data.screenSpecCreated) dropNotSaved('screen_spec');
            if (data.componentCatalogueCreated) dropNotSaved('component_catalogue');
            if (data.designTokensCreated) setDesignTokensCard({ ...data.designTokensCreated, status: 'draft' });

            // Server-side auto-approvals (UX Agent stages 3-8). Apply AFTER
            // the *Created setters so a same-turn "create + approve" lands
            // on `approved`, not `draft`. The map keys mirror the UX
            // artefact `type` field emitted by sessions.ts.
            if (data.artefactsApproved && data.artefactsApproved.length > 0) {
              const setterByType: Record<string, React.Dispatch<React.SetStateAction<{ artefactId: string; title: string; version: string; status: string } | null>>> = {
                user_journey_map: setJourneyMapCard,
                screen_inventory: setScreenInventoryCard,
                screen_spec: setScreenSpecCard,
                component_catalogue: setComponentCatalogueCard,
                design_tokens: setDesignTokensCard,
              };
              for (const a of data.artefactsApproved) {
                const setter = setterByType[a.type];
                if (setter) setter(prev => prev ? { ...prev, status: 'approved' } : prev);
              }
            }
            if (data.claudeDesignOffer) setClaudeDesignCard('offer');
            if (data.claudeDesignSkipped) setClaudeDesignCard(null);
            if (data.implementationPromptCreated) {
              setPromptCard({
                id: data.implementationPromptCreated.artefactId,
                title: data.implementationPromptCreated.title,
                content: data.implementationPromptCreated.promptContent,
              });
            }

            // Refresh existing artefact cards that weren't explicitly updated in the done event.
            // Handles cases where the agent updated an artefact but extraction didn't fire
            // (e.g. SAD regeneration where extractSADFromMessage didn't match).
            const refreshCard = async (id: string) => {
              const a = await apiFetch<{
                id: string;
                title: string;
                status: string;
                latestVersion: { versionNumber: string } | null;
                lastResyncedAt?: string | null;
                resyncReason?: string | null;
              }>(`/artefacts/${id}`);
              return {
                artefactId: a.id,
                title: a.title,
                version: a.latestVersion?.versionNumber ?? '1.0',
                status: a.status,
                lastResyncedAt: a.lastResyncedAt ?? null,
                resyncReason: a.resyncReason ?? null,
              };
            };
            // #3175 (REGSYNC.D) follow-on — SAD/ADR are ALSO re-fetched when this
            // turn created/regenerated them: the register splice
            // (renderSadFromRegister / renderAdrRisksFromRegister) runs
            // server-side right after the save, so the "resynced from the
            // register" marker is only on the row, not in the sadCreated /
            // adrCreated event payload that setSadCard/setAdrCard were just
            // built from. Without this the indicator would be missing on the
            // exact turn the resync happened.
            const sadRefreshId = data.sadCreated?.artefactId ?? session?.metadata?.sadArtefactId;
            const adrRefreshId = data.adrCreated?.artefactId ?? session?.metadata?.adrArtefactId;
            if (sadRefreshId) refreshCard(sadRefreshId).then(setSadCard).catch(() => {});
            if (adrRefreshId) refreshCard(adrRefreshId).then(setAdrCard).catch(() => {});
            if (!data.artefactCreated && session?.metadata?.prdArtefactId) refreshCard(session.metadata.prdArtefactId).then(setArtefactCard).catch(() => {});
            if (!data.storiesCreated && session?.metadata?.userStoriesArtefactId) refreshCard(session.metadata.userStoriesArtefactId).then(setStoriesCard).catch(() => {});

            // Handle dimension signals — prefer server-side detection from done event,
            // fall back to client-side parsing + PATCH
            if (data.dimensionsUpdated) {
              setDevDimensions(data.dimensionsUpdated);
              if (data.promptGenerationUnlocked !== undefined) {
                setPromptGenerationUnlocked(data.promptGenerationUnlocked);
              }
            } else if (signals.dimensionComplete && session) {
              const { dimension, summary, skipped } = signals.dimensionComplete;
              apiFetch<{ dimensions?: DevDimensions; promptGenerationUnlocked?: boolean }>(
                `/projects/${session.projectId}/sessions/${session.id}/dimensions`,
                {
                  method: 'PATCH',
                  body: JSON.stringify({ dimension, complete: !skipped, summary, skipped }),
                },
              ).then(updated => {
                if (updated.dimensions) setDevDimensions(updated.dimensions);
                if (updated.promptGenerationUnlocked !== undefined) {
                  setPromptGenerationUnlocked(updated.promptGenerationUnlocked);
                }
              }).catch(() => { /* non-critical */ });
            }
            if (signals.promptGenerationUnlocked) {
              setPromptGenerationUnlocked(true);
            }
            // Show notification when all dimensions just became complete
            const justUnlockedPrompt =
              (data.promptGenerationUnlocked === true) ||
              (signals.promptGenerationUnlocked === true);
            if (justUnlockedPrompt) {
              const autoCreated = Boolean(data.implementationPromptCreated);
              setTimeout(() => {
                setMessages(prev => {
                  if (prev.some(m => m.id === '__dim_complete_notify__')) return prev;
                  return [...prev, {
                    id: '__dim_complete_notify__',
                    role: 'notification' as const,
                    content: autoCreated
                      ? 'All three dimensions are complete. Your implementation prompt has been created — review it below.'
                      : 'All three dimensions are complete. Click Ready to generate ↑ above to create your implementation prompt.',
                    createdAt: new Date().toISOString(),
                  }];
                });
              }, 500);
            }

            if (signals.adrConflict) {
              setAdrConflictCards(prev => [
                ...prev,
                { id: `${Date.now()}`, ...signals.adrConflict! },
              ]);
            }
          } else if (data.type === 'register_override_blocked' || data.type === 'register_conflict_detected' || data.type === 'locked_section_patch_blocked' || data.type === 'artefact_locked_save_blocked') {
            // #3382 — a blocked save used to be invisible; show it.
            const info = parseSaveBlockedEvent(data);
            if (info) setSaveBlocked(prev => ({ ...prev, [info.artefactType]: info }));
          } else if (data.type === 'artefact_not_saved') {
            // #3708 — a UX artefact the agent wrote in chat was not saved; say so, per type.
            const info = parseArtefactNotSavedEvent(data);
            if (info) setNotSaved(prev => ({ ...prev, [info.artefactType]: info }));
          } else if (data.type === 'artefact_structure_rebuilt') {
            // #3851 — saved, but the structured data was rebuilt by BluePrint; say so.
            const info = parseStructureRebuiltEvent(data);
            if (info) setStructureRebuilt(prev => ({ ...prev, [info.artefactType]: info }));
          } else if (data.type === 'register_edit_report') {
            // #3383 — the SAD saved, but some register edits were not applied.
            const info = parseRegisterEditReportEvent(data);
            if (info) setRegisterEditReport(info);
          } else if (data.type === 'register_delta') {
            // #3583 -- what the SAD save did to the open-question register, in counts.
            const info = parseRegisterDeltaEvent(data);
            if (info) setRegisterDelta(info);
          } else if (data.type === 'error') {
            throw new Error(data.message ?? 'Streaming error');
          }
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User stopped generation — keep partial content as a real message
        setMessages(prev =>
          prev.map(m =>
            m.id === STREAMING_ID
              ? { ...m, id: `stopped_${Date.now()}`, content: m.content + '\n\n*(Generation stopped)*' }
              : m,
          ),
        );
      } else {
        // Defense-in-depth for the upload-too-large failure mode.
        // ChatInput pre-validates against the per-file size limit
        // (10 MB), but if a deployment's reverse proxy is set below
        // the application cap the upload still trips a 413 — and a
        // proxy that drops the connection mid-stream surfaces as a
        // generic "Failed to fetch" TypeError from fetch() rather
        // than a parseable HTTP response.
        //
        // The two failure shapes get different message treatments:
        //
        //   • 413 with attachments — unambiguous, body-too-large.
        //     Direct "max 10 MB" message.
        //   • "Failed to fetch" with attachments — could be a
        //     mid-stream proxy abort OR a legitimate offline /
        //     connectivity failure. The reviewer flagged that the
        //     prior wording mis-attributed offline drops as size
        //     failures, so the message now hedges: it mentions the
        //     size limit as one possibility while keeping
        //     connectivity as the other.
        const errMessage = err instanceof Error ? err.message : 'Failed to send message';
        const hadFiles = !!(files && files.length > 0);
        const friendlyMessage = (() => {
          if (!hadFiles) return errMessage;
          if (errMessage.includes('413')) {
            return 'Upload failed — the attachment exceeds the server’s size limit. Max 10 MB per file; try a smaller file.';
          }
          if (errMessage === 'Failed to fetch') {
            return 'Upload failed. Your connection may have dropped, or the attachment may exceed the server’s per-file size limit (10 MB).';
          }
          return errMessage;
        })();
        setError(friendlyMessage);
        setMessages(prev => prev.filter(m => m.id !== tempUserId && m.id !== STREAMING_ID));
      }
    } finally {
      abortControllerRef.current = null;
      // ACT.A4 (#1703) — keep `sending` true across a queued wait (see
      // `willRetry` above); only clear it once this attempt is truly done
      // (admitted-and-streamed, errored, or the user cancelled via Stop).
      if (!willRetry) setSending(false);
    }
  };

  const handleStop = () => {
    // ACT.A4 (#1703) — a pending queued-retry timer takes priority: by the
    // time it's pending, the original fetch has already completed and
    // abortControllerRef.current is null, so aborting it would be a no-op.
    // Cancel the timer and reset UI state directly instead.
    if (queueRetryTimeoutRef.current !== null) {
      clearTimeout(queueRetryTimeoutRef.current);
      queueRetryTimeoutRef.current = null;
      setQueueStatus(null);
      setSending(false);
      return;
    }
    abortControllerRef.current?.abort();
  };

  // Auto-send first message for gap analysis sessions (only if no existing history)
  const autoAnalyseTriggered = useRef<string | null>(null);
  useEffect(() => {
    if (!autoAnalyse || !session || sending) return;
    // Guard: only trigger once per session, and only if messages is just the welcome placeholder
    if (autoAnalyseTriggered.current === session.id) return;
    const hasRealMessages = messages.some(m => m.id !== '__welcome__');
    if (hasRealMessages) return;
    autoAnalyseTriggered.current = session.id;
    handleSend('Please perform a comprehensive gap analysis of the imported artefacts and present a structured report.');
  }, [autoAnalyse, session, sending, messages]);

  // Compute the active (first incomplete non-skipped) dimension. Returns
  // the key (for telemetry IDs / per-dimension lookups), display label,
  // and the Continue-button copy that names the next step. The label for
  // the *next* dimension is intentionally encoded here rather than derived
  // because dimension 3's "next" is prompt generation, not another
  // dimension — so the table is the single source of truth.
  const activeDimension = (() => {
    if (!devDimensions) return null;
    const map: { key: keyof DevDimensions; label: string; continueCopy: string }[] = [
      { key: 'codebaseContext',        label: 'Codebase Context',        continueCopy: 'Continue to Approach →' },
      { key: 'implementationApproach', label: 'Implementation Approach', continueCopy: 'Continue to AC Check →' },
      { key: 'acceptanceCriteria',     label: 'Acceptance Criteria',     continueCopy: 'Generate implementation prompt →' },
    ];
    for (const entry of map) {
      const d = devDimensions[entry.key];
      if (!d.complete && !d.skipped) return entry;
    }
    return null;
  })();

  // TODO(telemetry): wire to the BluePrint analytics pipeline when it
  // ships. Mirrors HelpPopover's deferred-telemetry pattern
  // (apps/web/src/components/help/HelpPopover.tsx:11-13, 166-181).
  //
  // Event shape:
  //   {
  //     event: 'dimension_action_clicked',
  //     properties: {
  //       action_id: 'dimension-continue-<key>' | 'dimension-skip-<key>',
  //       action:    'continue' | 'skip',
  //       dimension: 'codebaseContext' | 'implementationApproach' | 'acceptanceCriteria',
  //       session_id, project_id, tenant_id, user_id,   // from auth/session context
  //       timestamp:  ISO-8601,
  //     },
  //   }
  //
  // Fires on each click of either button (including dialog confirm for
  // skip). Drives AC #5 of #1116 — comparing continue-vs-skip frequency
  // validates whether the new primary affordance reduces confusion.
  const trackDimensionAction = (_action: 'continue' | 'skip', _key: keyof DevDimensions) => {
    // No-op until analytics pipeline lands. IDs:
    //   `dimension-continue-${_key}` / `dimension-skip-${_key}`
  };

  const handleSkipDimension = () => {
    if (!activeDimension) return;
    trackDimensionAction('skip', activeDimension.key);
    void handleSend('Skip and use defaults for this dimension');
    setSkipDialogOpen(false);
  };

  const handleContinueDimension = () => {
    if (!activeDimension) return;
    trackDimensionAction('continue', activeDimension.key);
    void handleSend('Looks good, please mark this dimension complete and move to the next dimension.');
  };

  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const handleGeneratePrompt = async () => {
    if (!session || !projectId) return;
    setGeneratingPrompt(true);
    try {
      // ACC.A2 follow-up (#848 Codex P2) — the /generate-prompt handler
      // now also runs runSelfReflection and returns a `devReflection`
      // field with the reflection artefact ID + headline fields. We
      // fetch the full JSONB content via /artefacts/:id (same path used
      // by restoreDevReflectionCard) and call setDevReflectionCard so
      // the ImplementationPromptCard gate is wired correctly without a
      // second-fetch race. Best-effort — if the backend's reflection
      // orchestration failed, devReflection is absent and we skip.
      const data = await apiFetch<{
        artefact: { id: string; title: string };
        promptContent: string;
        devReflection?: {
          artefactId: string;
          selfAssessment: 'READY_FOR_REVIEW' | 'NEEDS_REWORK';
          selfCorrectionAttempts: number;
        };
      }>(
        `/projects/${projectId}/sessions/${session.id}/generate-prompt`,
        { method: 'POST' },
      );
      setPromptCard({ id: data.artefact.id, title: data.artefact.title, content: data.promptContent });
      if (data.devReflection) {
        try {
          // Round 1 review (HIGH #2): latestVersion can be null for a
          // brand-new artefact whose version row is not yet visible to
          // a follow-up read (race between runSelfReflection's commit
          // and this fetch). Use optional-chaining to match the
          // restoreDevReflectionCard helper's defensive pattern at
          // line ~232 — without it a TypeError would silently swallow
          // into the bare catch and the gate would show "Ready to
          // generate" for a flagged prompt: the exact bypass #848
          // is meant to close.
          const a = await apiFetch<{ id: string; title: string; latestVersion: { notes: string | null } | null }>(
            `/artefacts/${data.devReflection.artefactId}`,
          );
          const raw = a.latestVersion?.notes ?? null;
          if (raw) {
            const content = JSON.parse(raw) as DevReflectionCardContent;
            setDevReflectionCard({ id: a.id, title: a.title, content });
          }
        } catch {
          // ignore — gate stays in "Ready to generate" state, Phase 4
          // EXIT gate will catch any genuine missing reflection later.
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate prompt');
    } finally {
      setGeneratingPrompt(false);
    }
  };

  const handleRetry = () => {
    console.log('[Retry] lastUserContent:', lastUserContent, 'lastUserFiles:', lastUserFiles);
    if (!lastUserContent) return;
    // Clear stale error banner and any orphaned streaming/optimistic messages before retrying
    setError('');
    setMessages(prev =>
      prev.filter(m => m.id !== STREAMING_ID && !m.id.startsWith('__opt_')),
    );
    void handleSend(lastUserContent, lastUserFiles);
  };

  // ── Session history drawer ──────────────────────────────────────────────────

  const fetchSessionList = useCallback(async () => {
    if (!projectId) return;
    setSessionListLoading(true);
    try {
      const list = await apiFetch<SessionSummary[]>(`/projects/${projectId}/sessions`);
      setSessionList(list);
    } catch {
      // silently fail — drawer just shows empty
    } finally {
      setSessionListLoading(false);
    }
  }, [projectId]);

  const openHistoryDrawer = useCallback(() => {
    setHistoryDrawerOpen(true);
    void fetchSessionList();
  }, [fetchSessionList]);

  const handleSelectSession = useCallback((s: SessionSummary) => {
    setHistoryDrawerOpen(false);
    if (s.id === session?.id) return; // already viewing this one
    // ChatPage guards initialisation with `initialized.current = true`, so a
    // pure react-router `navigate` call would change the URL but leave all
    // the page state pinned to the previous session. A full reload is the
    // simplest reliable way to re-init for the new session — drop the dead
    // `navigate` that the previous code paired with `window.location.href`
    // (the navigate ran briefly, then the href reload immediately threw it
    // away, leaving a confusing two-step transition in dev tools).
    const target = s.status === 'active'
      ? `/projects/${projectId}/chat?agentType=${s.agentName}`
      : `/projects/${projectId}/chat?sessionId=${s.id}`;
    window.location.href = target;
  }, [projectId, session?.id]);

  const handleNewChat = useCallback(async () => {
    if (!projectId || !session) return;
    setNewChatConfirmOpen(false);
    try {
      await apiFetch(`/projects/${projectId}/sessions`, {
        method: 'POST',
        body: JSON.stringify({ agentType: session.agentType, forceNew: true }),
      });
      // Full reload to pick up the new session
      window.location.href = `/projects/${projectId}/chat?agentType=${session.agentType}`;
    } catch (err) {
      console.error('[NewChat]', err);
    }
  }, [projectId, session]);

  // Friendly screen when no agent exists for the current phase (e.g. phase_3+)
  const isNoAgent = !loading && !!error && error.toLowerCase().includes('no agent');

  const phaseLabel = project?.currentPhase === 'phase_3_sprint_planning' ? 'Sprint Planning'
    : project?.currentPhase === 'phase_4_development' ? 'Development'
    : project?.currentPhase === 'phase_5_code_review' ? 'Code Review'
    : project?.currentPhase === 'phase_6_testing' ? 'Testing'
    : project?.currentPhase === 'phase_7_deployment' ? 'Deployment'
    : project?.currentPhase === 'phase_8_retrospective' ? 'Retrospective'
    : null;

  const getStaleness = (artefactType: string) => {
    const match = staleArtefacts.find(s => s.artefactType === artefactType);
    return match ? { isStale: true, stalenessReason: match.reason } : { isStale: false, stalenessReason: undefined };
  };

  // Sprint A.9 — ChatPage owns its internal scroll. The outer wrapper used to
  // be `h-screen overflow-hidden`. T-layout-4 (#1168) — resolved: under
  // ProjectLayout's `min-h-screen` this div had no bounded height and the page
  // body scrolled instead (verified in a real browser). The chat route now
  // mounts under ProjectChatLayout (`h-screen overflow-hidden`), which is what
  // lets this div claim exactly the remaining viewport.
  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ minHeight: 0 }}>
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0 px-6 py-4" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center gap-3">
            <Link
              to={projectId ? `/projects/${projectId}` : '/projects'}
              style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}
              className="transition-colors hover:text-[color:var(--primary)]"
            >
              ← Back to Project
            </Link>
            {project && (
              <>
                <span style={{ color: 'var(--border)' }}>/</span>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{project.name}</span>
                <GithubSourceBacklink githubSource={importedSource} />
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            {session && <AgentAvatar agentType={session.agentType} size={24} />}
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
              {session?.agentDisplayName ?? 'AI Agent'}
            </span>
            <span className="w-2 h-2 rounded-full" style={{ background: 'var(--success)' }} />

            {/* Separator */}
            {session && !isReadOnly && (
              <span className="w-px h-5" style={{ background: 'var(--border)' }} />
            )}

            {/* New Chat button */}
            {session && !isReadOnly && (
              <button
                onClick={() => setNewChatConfirmOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                style={{
                  fontFamily: 'var(--font-body)',
                  color: 'var(--primary)',
                  background: 'rgba(124,58,237,0.06)',
                  border: '1px solid rgba(124,58,237,0.15)',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.06)'; }}
                title="End this session and start a new one"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                New Chat
              </button>
            )}

            {/* History button */}
            <button
              onClick={openHistoryDrawer}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              style={{
                fontFamily: 'var(--font-body)',
                color: 'var(--text-secondary)',
                background: 'transparent',
                border: '1px solid var(--border)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-alt)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              title="View session history"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              History
            </button>
          </div>
        </div>

        {/* Read-only banner for historical sessions */}
        {isReadOnly && (
          <div className="flex-shrink-0 px-6 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700 flex items-center gap-2">
            <span>📖</span>
            Viewing archived session history
            {session?.agentDisplayName && ` · ${session.agentDisplayName}`}
            <div className="ml-auto flex items-center gap-3">
              <button onClick={openHistoryDrawer} className="hover:underline" style={{ color: 'var(--primary)' }}>
                All Sessions
              </button>
              <Link to={`/projects/${projectId}`} className="hover:underline" style={{ color: 'var(--primary)' }}>
                ← Back to Dashboard
              </Link>
            </div>
          </div>
        )}

        {/* No-agent coming-soon screen — replaces chat body */}
        {isNoAgent ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6 text-center">
            <AgentAvatar agentType="system" size={64} />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
              {phaseLabel ? `${phaseLabel} Agent — Coming Soon` : 'No Agent Available for This Phase'}
            </h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', maxWidth: 420 }}>
              {phaseLabel
                ? `The ${phaseLabel} Agent is not yet available. Check back in a future sprint.`
                : 'No agent is configured for the current phase.'}
            </p>
            <Link
              to={`/projects/${projectId}`}
              className="px-5 py-2 rounded-lg text-white text-sm font-medium transition-opacity hover:opacity-90"
              style={{ background: 'var(--primary)', fontFamily: 'var(--font-body)' }}
            >
              ← Back to Dashboard
            </Link>
          </div>
        ) : (
          <>
        {/* Stage progress indicator */}
        {!loading && (
          <AgentProgressBar
            stage={agentState.stage}
            dimension={agentState.dimension}
            complete={agentState.complete}
            stages={session?.stages}
            totalDimensions={session?.agentType ? (AGENT_DIMENSIONS[session.agentType] ?? 6) : 6}
            staleStages={(() => {
              const map = session?.agentType ? ARTEFACT_STAGE_MAP[session.agentType] : undefined;
              if (!map || staleArtefacts.length === 0) return undefined;
              const stages: number[] = [];
              for (const sa of staleArtefacts) {
                const stageNum = map[sa.artefactType];
                if (stageNum && !stages.includes(stageNum)) stages.push(stageNum);
              }
              return stages.length > 0 ? stages : undefined;
            })()}
            artefactStages={session?.agentType === 'ux_design' ? {
              // #3708 — a UX stage is ticked only when its artefact is saved (a card exists).
              3: !!journeyMapCard,
              5: !!screenInventoryCard,
              6: !!screenSpecCard,
              7: !!componentCatalogueCard,
              8: !!designTokensCard,
            } : undefined}
          />
        )}

        {/* Dimension planning progress (Dev Agent only) */}
        {!loading && session?.agentType === 'dev_agent' && devDimensions && (
          <DimensionProgress
            dimensions={devDimensions}
            promptGenerationUnlocked={promptGenerationUnlocked}
            generating={generatingPrompt}
            onGeneratePrompt={() => void handleGeneratePrompt()}
          />
        )}

        {error && (
          <div className="mx-6 mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2 flex-shrink-0">
            {error}
          </div>
        )}

        {Object.entries(saveBlocked).map(([t, info]) => <SaveBlockedBanner key={t} info={info} />)}
        {recoverNote && (
          <div role="status" data-testid="artefact-recover-note" className="mx-6 mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 flex-shrink-0 flex items-start justify-between gap-3">
            <span>{recoverNote}</span>
            <button type="button" aria-label="Dismiss" onClick={() => setRecoverNote(null)}>×</button>
          </div>
        )}
        {session && (['screen_spec', 'component_catalogue'] as const).map(t => {
          const info = notSaved[t];
          if (!info) return null;
          return (
            <ArtefactNotSavedBanner
              key={t}
              info={info}
              sessionId={session.id}
              onDismiss={() => dropNotSaved(t)}
              onClosed={(message) => { dropNotSaved(t); setRecoverNote(message); }}
              canRecover={!isReadOnly}
              onRecovered={(o) => {
                const card = { artefactId: o.artefactId, title: o.title, version: o.version, status: 'draft' };
                if (t === 'screen_spec') setScreenSpecCard(card);
                else setComponentCatalogueCard(card);
                dropNotSaved(t);
                // #3877 (R1 P2-1) — a recovery whose structure was rebuilt says so, as the live save does.
                // Delta R2: only a known source counts as rebuilt, and a plain recovery clears an older note.
                setRecoverNote(structureRebuiltSourceLabel(o.structureRebuiltFrom) ? recoverOutcomeText(o) : null);
              }}
            />
          );
        })}
        {(['screen_spec', 'component_catalogue', 'design_tokens'] as const).map(t => {
          const info = structureRebuilt[t];
          return info ? <StructureRebuiltNotice key={`rebuilt-${t}`} info={info} onDismiss={() => setStructureRebuilt(prev => dropKey(prev, t))} /> : null;
        })}
        {registerEditReport && <RegisterEditReportBanner info={registerEditReport} />}
        {registerDelta && <RegisterDeltaLine info={registerDelta} />}

        {/* ACT.A4 (#1703) — per-tenant session queue indicator. Shown while
            the server has us queued (202) and is about to auto-retry. */}
        {queueStatus && (
          <div className="mx-6 mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2 flex-shrink-0">
            Server is busy — you are #{queueStatus.position} in line, retrying in ~{queueStatus.waitSeconds}s...
          </div>
        )}

        {/* Scrollable area: issue picker / selected issue / agent banner / messages / artefact cards / conflict cards.
            All non-fixed content lives here so the cumulative height of fixed
            siblings (header + stage bar + dim progress + input) cannot exceed
            viewport — see fix/dev-agent-chat-scroll-layout. */}
        <div data-testid="chat-scroll-area" style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* Issue Picker — Dev Agent (Phase 4 implementation) and Code
              Review Agent (Phase 5; ACC.A3 absorbs #816). Both agents
              share the picker UI; backend filters openIssues per agent
              type (dev_agent → all OPEN issues; code_review → only
              issues with at least one dev_reflection artefact). */}
          {!loading && (session?.agentType === 'dev_agent' || session?.agentType === 'code_review') && (!session.metadata?.selectedIssueId || changingIssue) && openIssues && projectId && (
            <div data-testid="issue-picker-wrapper" className="px-6 pt-4">
              <IssuePicker
                projectId={projectId}
                sessionId={session.id}
                openIssues={openIssues}
                onIssueSelected={(issue, contextMessage) => {
                  const isSwitch = changingIssue && messages.length > 1;
                  // Add visual separator if switching issue within existing session
                  if (isSwitch) {
                    setMessages(prev => [...prev, {
                      id: `separator-${Date.now()}`,
                      sessionId: session.id,
                      tenantId: '',
                      role: 'separator',
                      content: `Issue changed: ${issue.title}`,
                      createdAt: new Date().toISOString(),
                    }]);
                  }
                  setSession(prev => prev ? { ...prev, metadata: { ...prev.metadata, selectedIssueId: issue.id } } : prev);
                  setSelectedIssue(issue);
                  setChangingIssue(false);

                  // Auto-inject context message for fresh selections (not resume with existing conversation)
                  // For switches, also auto-inject so the agent knows about the new issue
                  const shouldAutoInject = contextMessage && (messages.length <= 1 || isSwitch);
                  if (shouldAutoInject) {
                    // Use setTimeout to let state updates settle before sending
                    setTimeout(() => void handleSend(contextMessage), 0);
                  }
                }}
              />
            </div>
          )}

          {/* Issue #855 — PR Picker for Code Review Agent. Shown after the
              reviewer has selected an issue but before they've selected a
              PR. agentType === 'code_review' is the Phase-5 implication —
              CRA sessions are only ever created at Phase 5 (the
              agent-picker entry point applies that check at session
              creation). Defense-in-depth phase guard isn't added here
              because the local Session interface doesn't carry the phase
              field; lifting it would be a separate type extension. */}
          {!loading
            && session?.agentType === 'code_review'
            && session.metadata?.selectedIssueId
            && !(session.metadata as Record<string, unknown> | undefined)?.prNumber
            && projectId && (
              <div data-testid="pr-picker-wrapper" className="px-6 pt-4">
                <PRPickerPanel
                  projectId={projectId}
                  onSelect={async (pr) => {
                    try {
                      const res = await apiFetch<{
                        session: typeof session;
                        contextMessage: string;
                      }>(`/projects/${projectId}/sessions/${session.id}/pr`, {
                        method: 'PATCH',
                        body: JSON.stringify({ prNumber: pr.number }),
                      });
                      if (res.session) setSession(res.session);
                      if (res.contextMessage) {
                        // Auto-inject context message so the CRA proceeds
                        // with the review without the user having to retype.
                        setTimeout(() => void handleSend(res.contextMessage), 0);
                      }
                    } catch (err) {
                      console.error('[#855] PR selection failed:', err);
                    }
                  }}
                />
              </div>
            )}

          {/* Selected Issue Card — Dev Agent + Code Review Agent
              (ACC.A3 / #816). Shown when an issue is selected and the
              user isn't currently switching. Same shape for both
              agents; the Change-issue control re-enters the picker. */}
          {!loading && (session?.agentType === 'dev_agent' || session?.agentType === 'code_review') && session.metadata?.selectedIssueId && !changingIssue && selectedIssue && (
            <div data-testid="selected-issue-card" className="px-6 pt-4">
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)',
                borderRadius: 'var(--radius-sm)', flexWrap: 'wrap',
              }}>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                  Working on:
                </span>
                <strong style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', flex: 1 }}>
                  {selectedIssue.title}
                </strong>
                {selectedIssue.priority && (
                  <span style={{
                    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
                    padding: '2px 7px', borderRadius: 'var(--radius-sm)',
                    background: selectedIssue.priority === 'P0' ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.12)',
                    color: selectedIssue.priority === 'P0' ? 'var(--warning)' : '#818cf8',
                    border: selectedIssue.priority === 'P0' ? '1px solid rgba(245,158,11,0.3)' : '1px solid rgba(99,102,241,0.25)',
                  }}>
                    {selectedIssue.priority}
                  </span>
                )}
                {selectedIssue.storyPoints != null && (
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {selectedIssue.storyPoints}pts
                  </span>
                )}
                <button
                  onClick={() => { setChangeIssueChoice('new'); setChangeIssueModalOpen(true); }}
                  style={{
                    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
                    padding: '3px 10px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)', background: 'transparent',
                    color: 'var(--text-secondary)', cursor: 'pointer',
                  }}
                >
                  Change issue
                </button>
              </div>
            </div>
          )}

          {/* Agent identity banner (only shown when no messages yet) */}
          {!loading && messages.length <= 1 && messages[0]?.id === '__welcome__' && session && (
            <div data-testid="agent-identity-banner" className="px-6 pt-6 pb-2">
              <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.12)' }}>
                <div className="flex-shrink-0">
                  <AgentAvatar agentType={session.agentType} size={40} />
                </div>
                <div>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                    {session.agentDisplayName ?? 'AI Agent'}
                  </p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {session.agentDescription ?? 'Your AI assistant for this project phase'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ACC.A1 #809 — resume banner sits above the chat history,
              independent of welcomeMessage. Renders nothing when null
              (fresh session, unsupported agent, or read failure on
              the server).

              Note on the autoAnalyse / gap-analysis branch above: those
              flows hit the same /sessions/:id endpoint that now returns
              `resumeBanner` in its payload. Gap-analysis sessions are
              created fresh per request, so `buildResumeBanner` returns
              null for them (no checkpoint row exists yet) and this
              component renders nothing — the desired behaviour. If
              future code starts seeding checkpoints into gap-analysis
              sessions, revisit whether the banner should suppress on
              that mode explicitly via session.metadata?.mode. */}
          <ResumeBanner message={session?.resumeBanner ?? null} />

          {/* Messages */}
          <ChatHistory
            messages={messages}
            loading={loading}
            streamingId={sending ? STREAMING_ID : undefined}
            agentType={session?.agentType ?? 'system'}
            onSendMessage={content => void handleSend(content)}
            readOnly={isReadOnly}
          />

          {/* Artefact cards — PRD then User Stories then SAD then ADRs */}
          {artefactCard && (
            <ArtefactCard
              artefactId={artefactCard.artefactId}
              projectId={projectId!}
              title={artefactCard.title}
              version={artefactCard.version}
              type="prd"
              status={artefactCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setArtefactCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              {...getStaleness('prd')}
              {...prototypeStatus}
              onPrototypeRegenerate={refreshPrototypeStatus}
              unresolvedOpenQuestionsCount={openQuestionsCount}
              undisposedRisksCount={undisposedRisksCount}
              undisposedNfrsCount={undisposedNfrsCount}
            />
          )}
          {storiesCard && (
            <ArtefactCard
              artefactId={storiesCard.artefactId}
              projectId={projectId!}
              title={storiesCard.title}
              version={storiesCard.version}
              type="user_stories"
              status={storiesCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setStoriesCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              {...getStaleness('user_stories')}
            />
          )}
          {sadCard && (
            <ArtefactCard
              artefactId={sadCard.artefactId}
              projectId={projectId!}
              title={sadCard.title}
              version={sadCard.version}
              type="sad"
              status={sadCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setSadCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              {...getStaleness('sad')}
              lastResyncedAt={sadCard.lastResyncedAt}
              resyncReason={sadCard.resyncReason}
            />
          )}
          {adrCard && (
            <ArtefactCard
              artefactId={adrCard.artefactId}
              projectId={projectId!}
              title={adrCard.title}
              version={adrCard.version}
              type="adr"
              status={adrCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setAdrCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              {...getStaleness('adr')}
              lastResyncedAt={adrCard.lastResyncedAt}
              resyncReason={adrCard.resyncReason}
            />
          )}
          {sprintPlanCard && (
            <ArtefactCard
              artefactId={sprintPlanCard.artefactId}
              projectId={projectId!}
              title={sprintPlanCard.title}
              version={sprintPlanCard.version}
              type="sprint_plan"
              status={sprintPlanCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setSprintPlanCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              {...getStaleness('sprint_plan')}
            />
          )}
          {backlogCard && (
            <ArtefactCard
              artefactId={backlogCard.artefactId}
              projectId={projectId!}
              title={backlogCard.title}
              version={backlogCard.version}
              type="sprint_backlog"
              status={backlogCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setBacklogCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              {...getStaleness('sprint_backlog')}
            />
          )}
          {backlogCard?.linkNote && (
            <p role="status" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
              {backlogCard.linkNote}
            </p>
          )}

          {changeOrderCard && (
            <ArtefactCard
              artefactId={changeOrderCard.artefactId}
              projectId={projectId!}
              title={changeOrderCard.title}
              version={changeOrderCard.version}
              type="change_order"
              status={changeOrderCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setChangeOrderCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
            />
          )}

          {qaReportCard && (
            <ArtefactCard
              artefactId={qaReportCard.artefactId}
              projectId={projectId!}
              title={qaReportCard.title}
              version={qaReportCard.version}
              type="qa_report"
              status={qaReportCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setQaReportCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
            />
          )}

          {analysisReportCard && (
            <ArtefactCard
              artefactId={analysisReportCard.artefactId}
              projectId={projectId!}
              title={analysisReportCard.title}
              version={analysisReportCard.version}
              type="analysis_report"
              status={analysisReportCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setAnalysisReportCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
            />
          )}

          {/* Phase 2.5 UX Design artefact cards. Standalone sessions (UX.S1)
              shouldn't surface a phase-handoff button on the design_tokens
              card — there's no Phase 2.5 → 3 transition to make. */}
          {journeyMapCard && (
            <ArtefactCard
              artefactId={journeyMapCard.artefactId}
              projectId={projectId!}
              title={journeyMapCard.title}
              version={journeyMapCard.version}
              type="user_journey_map"
              status={journeyMapCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setJourneyMapCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              {...getStaleness('user_journey_map')}
            />
          )}
          {screenInventoryCard && (
            <ArtefactCard
              artefactId={screenInventoryCard.artefactId}
              projectId={projectId!}
              title={screenInventoryCard.title}
              version={screenInventoryCard.version}
              type="screen_inventory"
              status={screenInventoryCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setScreenInventoryCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              {...getStaleness('screen_inventory')}
            />
          )}
          {screenSpecCard && (
            <ArtefactCard
              artefactId={screenSpecCard.artefactId}
              projectId={projectId!}
              title={screenSpecCard.title}
              version={screenSpecCard.version}
              type="screen_spec"
              status={screenSpecCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setScreenSpecCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              {...getStaleness('screen_spec')}
            />
          )}
          {componentCatalogueCard && (
            <ArtefactCard
              artefactId={componentCatalogueCard.artefactId}
              projectId={projectId!}
              title={componentCatalogueCard.title}
              version={componentCatalogueCard.version}
              type="component_catalogue"
              status={componentCatalogueCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setComponentCatalogueCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              {...getStaleness('component_catalogue')}
            />
          )}
          {designTokensCard && (
            <ArtefactCard
              artefactId={designTokensCard.artefactId}
              projectId={projectId!}
              title={designTokensCard.title}
              version={designTokensCard.version}
              type="design_tokens"
              status={designTokensCard.status}
              onSendMessage={content => void handleSend(content)}
              onStatusChange={s => setDesignTokensCard(prev => prev ? { ...prev, status: s } : null)}
              onNavigate={navigate}
              isStandalone={session?.metadata?.mode === 'standalone'}
              {...getStaleness('design_tokens')}
            />
          )}

          {/* #3704 — the Claude Design round trip: the offer card, or upload-only under the agent's
              request for a seed bundle. */}
          {session?.agentType === 'ux_design' && (claudeDesignCard || wantsClaudeDesignUpload(messages)) && (
            <ClaudeDesignRoundTripCard
              projectId={session.projectId}
              mode={claudeDesignCard ?? 'upload'}
              readOnly={isReadOnly}
              onSendMessage={content => handleSend(content)}
            />
          )}

          {/* Implementation Prompt card */}
          {promptCard && session && (
            <ImplementationPromptCard
              artefact={promptCard}
              sessionId={session.id}
              projectId={projectId!}
              generating={generatingCode && !codeGenComplete}
              reflection={devReflectionCard ? {
                selfAssessment:      devReflectionCard.content.selfAssessment,
                shortcutsIdentified: devReflectionCard.content.shortcutsIdentified,
                scopeAdditions:      devReflectionCard.content.scopeAdditions,
              } : null}
              onGenerateCode={() => { setGeneratingCode(true); }}
              onRequestChanges={prefill => { setInputText(prefill); inputRef.current?.focus(); }}
            />
          )}
          {/* ACC.B5 (#419) — Dev Agent self-correction attempts, rendered
              chronologically ahead of the DevReflectionCard summary.
              Each attempt is one regeneration cycle's outcome. Live
              updates arrive via the `dev_reflection_attempt_created`
              SSE event; reloads restore from agent_messages with
              message_type='dev_reflection_attempt'. */}
          {attempts.map((attempt, idx) => (
            // Composite key — attemptNumber is canonically unique within
            // a single session's loop run, but using an index suffix
            // hardens against any theoretical race where the SSE handler
            // and the restore path both contribute to state for the same
            // attempt number (extremely unlikely in practice but cheap
            // to defend against).
            <AttemptCard key={`${attempt.attemptNumber}-${idx}`} attempt={attempt} />
          ))}
          {/* ACC.A2 (#412) — Dev Agent self-review card, mounted directly
              after the implementation prompt card. Renders nothing when
              null (no reflection produced for this session yet).
              ACC.B5 (#419) — `attempts` prop drives the three-count
              summary; falls back to a final-state derivation when
              empty (legacy reflections). */}
          {devReflectionCard && (
            <DevReflectionCard artefact={devReflectionCard} attempts={attempts} />
          )}
          {/* Issue #855 — Code Review Report card. Mirrors the
              DevReflectionCard mount pattern; surfaced when the
              code_review_report_created SSE event fires (handled in the
              SSE switch above). Renders nothing when null. */}
          {codeReviewCard && (
            <CodeReviewReportCard
              artefactId={codeReviewCard.id}
              report={codeReviewCard.content}
            />
          )}
          {generatingCode && session && projectId && (
            <DevResponsePanel
              projectId={projectId}
              sessionId={session.id}
              issueId={session.metadata?.selectedIssueId}
              onComplete={(_artefactId, _usage) => {
                setCodeGenComplete(true);
              }}
            />
          )}

          {/* ADR conflict warning cards */}
          {adrConflictCards.map(card => (
            <div
              key={card.id}
              style={{
                margin: '4px 16px 0',
                padding: '10px 14px',
                background: 'rgba(245,158,11,0.08)',
                border: '1px solid rgba(245,158,11,0.3)',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 10,
                flexShrink: 0,
              }}
            >
              <div>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--warning)' }}>
                  ⚠ ADR Conflict — {card.adr}:
                </span>{' '}
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                  {card.description}
                </span>
              </div>
              <button
                onClick={() => setAdrConflictCards(prev => prev.filter(c => c.id !== card.id))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: 14,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        {/* Bottom stack — measured as one element; see bottomStackEl above.
            Everything that renders below the scroll area lives inside it so
            the "?" offset clears all of it, not just ChatInput. */}
        <div ref={setBottomStackEl} data-testid="chat-bottom-stack" style={{ flexShrink: 0 }}>
        {/* Input — pinned at bottom. Hidden entirely on archived sessions
            so the read-only view doesn't show a greyed-out box that the
            user cannot interact with — see fix/session-history-conversation-view. */}
        {!isReadOnly && (
          <ChatInput
            ref={inputRef}
            value={inputText}
            onChange={handleInputChange}
            sessionId={session?.id}
            onSend={handleSend}
            disabled={sending || loading}
            onRetry={error ? handleRetry : undefined}
            onStop={sending ? handleStop : undefined}
          />
        )}
        {isReadOnly && (
          <div
            data-testid="archived-session-footer"
            style={{
              flexShrink: 0,
              padding: '12px 24px',
              borderTop: '1px solid var(--border)',
              background: 'var(--surface-alt)',
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              color: 'var(--text-secondary)',
              textAlign: 'center',
            }}
          >
            End of archived session — this conversation has ended.
          </div>
        )}

        {/* Dimension navigation: primary Continue + secondary Skip
            (Dev Agent only, when there's an active dimension). Renders
            for every dimension via the activeDimension gate, so AC #3
            (consistent treatment for D1/D3) is satisfied by the same
            block — no per-dimension surfaces needed. */}
        {session?.agentType === 'dev_agent' && !isReadOnly && activeDimension && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '8px 16px 12px', flexShrink: 0 }}>
            <button
              onClick={handleContinueDimension}
              disabled={sending}
              data-testid="dimension-continue-button"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                fontWeight: 600,
                padding: '10px 20px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: 'var(--primary)',
                color: '#fff',
                cursor: sending ? 'not-allowed' : 'pointer',
                opacity: sending ? 0.6 : 1,
                minWidth: 260,
              }}
            >
              {activeDimension.continueCopy}
            </button>
            <button
              onClick={() => setSkipDialogOpen(true)}
              disabled={sending}
              data-testid="dimension-skip-button"
              title="Bypass this dimension without confirming its output. Not recommended."
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 12,
                color: 'var(--text-secondary)',
                background: 'none',
                border: 'none',
                cursor: sending ? 'not-allowed' : 'pointer',
                textDecoration: 'underline',
                padding: '2px 6px',
                opacity: sending ? 0.6 : 1,
              }}
            >
              Skip this dimension review (advanced)
            </button>
          </div>
        )}
        </div>

        {/* Skip dimension confirmation dialog */}
        {skipDialogOpen && activeDimension && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
            onClick={() => setSkipDialogOpen(false)}
          >
            <div
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-xl)',
                padding: '24px',
                maxWidth: 380,
                width: '90%',
                maxHeight: '85vh',
                overflowY: 'auto',
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
                Skip {activeDimension.label}?
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
                This dimension helps the agent generate more accurate prompts. Are you sure you want to skip it?
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  onClick={() => setSkipDialogOpen(false)}
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    fontWeight: 500,
                    padding: '7px 16px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    background: 'transparent',
                    color: 'var(--text)',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSkipDimension}
                  disabled={sending}
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    fontWeight: 600,
                    padding: '7px 16px',
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    background: 'rgba(245,158,11,0.15)',
                    color: 'var(--warning)',
                    cursor: sending ? 'not-allowed' : 'pointer',
                    opacity: sending ? 0.6 : 1,
                  }}
                >
                  Skip anyway
                </button>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      {/* Change Issue modal */}
      {changeIssueModalOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => setChangeIssueModalOpen(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)', padding: 24, maxWidth: 460, width: '90%',
              maxHeight: '85vh', overflowY: 'auto',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }}
          >
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>
              Change issue?
            </h3>

            {/* Radio option: switch in session */}
            <label
              style={{
                display: 'flex', gap: 10, padding: '12px 14px', borderRadius: 'var(--radius-sm)',
                border: `1px solid ${changeIssueChoice === 'switch' ? 'var(--primary)' : 'var(--border)'}`,
                background: changeIssueChoice === 'switch' ? 'rgba(124,58,237,0.04)' : 'transparent',
                cursor: 'pointer', marginBottom: 10,
              }}
            >
              <input
                type="radio" name="changeIssue" value="switch"
                checked={changeIssueChoice === 'switch'}
                onChange={() => setChangeIssueChoice('switch')}
                style={{ accentColor: 'var(--primary)', marginTop: 2 }}
              />
              <div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                  Switch issue in this session
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4, marginTop: 2 }}>
                  The current conversation stays visible. Best for related issues in the same area.
                </div>
              </div>
            </label>

            {/* Radio option: new session */}
            <label
              style={{
                display: 'flex', gap: 10, padding: '12px 14px', borderRadius: 'var(--radius-sm)',
                border: `1px solid ${changeIssueChoice === 'new' ? 'var(--primary)' : 'var(--border)'}`,
                background: changeIssueChoice === 'new' ? 'rgba(124,58,237,0.04)' : 'transparent',
                cursor: 'pointer', marginBottom: 20,
              }}
            >
              <input
                type="radio" name="changeIssue" value="new"
                checked={changeIssueChoice === 'new'}
                onChange={() => setChangeIssueChoice('new')}
                style={{ accentColor: 'var(--primary)', marginTop: 2 }}
              />
              <div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                  Start a new session for the new issue
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4, marginTop: 2 }}>
                  A fresh conversation with no prior context. This session is saved and can be resumed.
                </div>
              </div>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setChangeIssueModalOpen(false)}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                disabled={creatingNewSession}
                onClick={() => {
                  setChangeIssueModalOpen(false);
                  if (changeIssueChoice === 'switch') {
                    setChangingIssue(true);
                  } else {
                    // Create a new session and navigate
                    if (!projectId) return;
                    setCreatingNewSession(true);
                    apiFetch<{ id: string }>(`/projects/${projectId}/sessions`, {
                      method: 'POST',
                      body: JSON.stringify({ agentType: 'dev_agent', forceNew: true }),
                    }).then(() => {
                      // Navigate to fresh chat page (POST /sessions will resume the new active session)
                      window.location.href = `/projects/${projectId}/chat?agentType=dev_agent`;
                    }).catch(() => {
                      setCreatingNewSession(false);
                    });
                  }
                }}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: 'none', background: 'var(--primary)', color: '#fff',
                  cursor: creatingNewSession ? 'not-allowed' : 'pointer',
                  opacity: creatingNewSession ? 0.6 : 1,
                }}
              >
                {creatingNewSession ? 'Creating...' : 'Continue →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Chat confirmation dialog */}
      {newChatConfirmOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setNewChatConfirmOpen(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)', padding: 24, maxWidth: 400, width: '90%',
              maxHeight: '85vh', overflowY: 'auto',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }}
          >
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              Start new session?
            </h3>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 20 }}>
              The current session will be saved and you can access it later from the session history.
              A fresh {session?.agentDisplayName ?? 'agent'} session will be created.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setNewChatConfirmOpen(false)}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => void handleNewChat()}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  border: 'none', background: 'var(--primary)', color: '#fff',
                  cursor: 'pointer',
                }}
              >
                New Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session history drawer */}
      <SessionHistoryDrawer
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        sessions={sessionList}
        currentSessionId={session?.id}
        onSelectSession={handleSelectSession}
        loading={sessionListLoading}
      />
    </div>
  );
}
